// components/chat/ChatPopup.tsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { socketManager, useSocketEmit } from "../../lib/socketManager";
import { useCompanyFeatures } from '../../contexts/CompanyFeaturesContext';
import { usePageVisibility } from '../../hooks/usePageVisibility';
import MicButton from '../MicButton';
import { SPEECH_LANG_OPTIONS, SPEECH_LANG_CONFIG } from '../../constants/speechLang';
import { LangCode } from '../../hooks/useSpeechToText';
import { 
  markMessagesRead, 
  sendChatMessage, 
  sendAIChatMessage, 
  createConversation, 
  createAIConversation,
  bulkDeleteMessages, 
  deleteMessage,
  getConversations,
  getMessages,
  getPresence,
  searchConversations,
  updateConversationParticipants,
  removeParticipant,
  removeParticipantFromConversation,
  addMessageReaction,
  removeMessageReaction
} from '../../api/chatApi';
import { apiClient } from '../../api/apiClient';
import { toast } from 'react-toastify';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';


const T = {
  botName: "לומירה AI",
  // status
  statusYour: "הסטטוס שלך",
  statusOnline: "מחובר",
  statusDnd: "לא להפריע",
  statusOffline: "לא מחובר",
  // header / actions
  back: "חזרה",
  close: "סגור",
  expand: "הרחב",
  restore: "שחזר",
  // list / browse
  browse: "עיון",
  searchPlaceholder: "חיפוש אנשים או שיחות…",
  messages: "הודעות",
  newDM: "הודעה ישירה חדשה",
  newGroup: "קבוצת צ׳אט חדשה",
  lumiraAI: "לומירה AI",
  lumiraOpen: "פתח את לומירה AI",
  lumiraPremium: "תוסף פרימיום",
  // conversation list previews
  noMessages: "אין הודעות עדיין",
  typingDm: "מקליד…",
  typingGroupOne: (name: string) => `${name} מקליד…`,
  typingGroupTwo: (a: string, b: string) => `${a} ו-${b} מקלידים…`,
  // group members panel
  membersTitle: (title?: string) => `חברים — ${title || "קבוצה"}`,
  addMembers: "הוסף חברים",
  add: "הוסף",
  member: "חבר",
  admin: "אדמין",
  everyoneAlready: "כולם כבר בקבוצה.",
  cancel: "ביטול",
  adding: "מוסיף…",
  addN: (n: number) => `הוסף ${n} חבר${n > 1 ? "ים" : ""}`,
  removing: "מוחק…",
  removeMember: "הסר חבר",
  // compose area
  writeMessage: "כתוב הודעה…",
  askBot: (bot: string) => `שאל את ${bot}…`,
  send: "שלח",
  // selection bar
  selected: (n: number) => `${n} נבחרו`,
  selectAll: "בחר הכל",
  copy: "העתק",
  forward: "העבר",
  delete: "מחק",
  // tooltips / receipts
  delivered: "נמסר",
  read: "נקרא",
  seenBy: "נראה על ידי",
  viewWhoSaw: "צפה במי ראה",
  // misc days
  today: "היום",
  yesterday: "אתמול",
  // dialogs / errors / system
  groupNamePlaceholder: "שם קבוצה…",
  create: "צור",
  dmInvalidId: "מזהה משתמש לא תקין",
  forwardedPrefix: "הועבר:\n",
  loading: "טוען…",
  msgDeleted: "הודעה נמחקה",
  aiAddonMsg: "לומירה AI הוא תוסף. שלחו לנו הודעה כדי להפעיל אותו לחברה שלכם.",
  aiFail: "מצטער — לא הצלחתי לענות כעת. נסו שוב.",
};
/* --- Minimal inline icons (size-aware) --- */
const ChatIcon = (
  { size = 18, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }
) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" {...props}>
    <path d="M20 15H13.333L8 19v-4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4h13a4 4 0 0 1 4 4v4a4 4 0 0 1-4 4Z" fill="currentColor" opacity=".14"/>
    <path d="M7 5h13v8h-7.667L8 16v-3H7V5Z" stroke="currentColor" strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="10" cy="9.5" r=".9" fill="currentColor" />
    <circle cx="14" cy="9.5" r=".9" fill="currentColor" />
    <circle cx="18" cy="9.5" r=".9" fill="currentColor" />
  </svg>
);
const UserPlusIcon = ({ size = 18, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
    <circle cx="9" cy="8" r="4" stroke="currentColor" strokeWidth="1.3" fill="none"/>
    <path d="M3 20a6 6 0 0 1 12 0" stroke="currentColor" strokeWidth="1.3" fill="none"/>
    <path d="M17 7v6M14 10h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const UsersIcon = ({ size = 18, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
    <circle cx="8" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.3" fill="none"/>
    <circle cx="17" cy="8" r="2.8" stroke="currentColor" strokeWidth="1.3" fill="none"/>
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" stroke="currentColor" strokeWidth="1.3" fill="none"/>
    <path d="M13.5 14.5a5 5 0 0 1 7 5.5" stroke="currentColor" strokeWidth="1.3" fill="none"/>
  </svg>
);
const RobotIcon = ({ size = 18, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
    <rect x="4" y="6.5" width="16" height="11" rx="3" stroke="currentColor" strokeWidth="1.3" fill="none"/>
    <circle cx="9" cy="12" r="1.1" fill="currentColor"/>
    <circle cx="15" cy="12" r="1.1" fill="currentColor"/>
    <path d="M12 3v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
/* Small eye icon for "Seen by" button */
const EyeIcon = ({ size = 14, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" {...props}>
    <path d="M12 5c5.5 0 9 5.5 9 7s-3.5 7-9 7-9-5.5-9-7 3.5-7 9-7Z" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="12" cy="12" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.4"/>
  </svg>
);

const ArrowLeftIcon = ({ size = 16, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" {...props}>
    <path d="M14 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const XIcon = ({ size = 16, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" {...props}>
    <path d="M6 6l12 12M18 6l-12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const CheckSquareIcon = ({ size = 16, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" {...props}>
    <rect x="4" y="4" width="16" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="1.6"/>
    <path d="M8 12l3 3 5-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ChevronDownIcon = ({ size = 14, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} {...props}>
    <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/** Minimal icon button with glass/blur look */
function IconButton({
  title, onClick, active, children, danger
}: { title: string; onClick: () => void; active?: boolean; danger?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={[
        "relative inline-flex items-center justify-center",
        "h-8 px-2 rounded-xl text-xs font-medium transition",
        "backdrop-blur bg-white/70 hover:bg-white shadow-sm border border-gray-200",
        active ? "ring-2 ring-blue-300 text-blue-700" : "",
        danger ? "hover:bg-red-50 text-red-600" : ""
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/** Compact status dropdown (Online / DND / Offline) */
function StatusChip({
  value, onChange
}: { value: "online" | "dnd" | "offline"; onChange: (v: "online" | "dnd" | "offline") => void }) {
  const [open, setOpen] = React.useState(false);
  const opts = [
    { k: "online",  label: T.statusOnline,          dot: "bg-green-500" },
    { k: "dnd",     label: T.statusDnd,  dot: "bg-red-500"   },
    { k: "offline", label: T.statusOffline,         dot: "bg-gray-400"  },
  ] as const;

  const curr = opts.find(o => o.k === value)!;

  return (
    <div className="relative" tabIndex={0} onBlur={() => setOpen(false)}>
      <button
        type="button"
        title="Your status"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-2.5 h-9 pl-3 pr-2 rounded-xl text-xs font-medium
                   backdrop-blur bg-white/70 hover:bg-white shadow-sm border border-gray-200 transition-colors"
      >
        <span className={`inline-block w-3 h-3 rounded-full ${curr.dot} flex-shrink-0`} />
        <span className="hidden sm:inline text-gray-700 font-semibold">{curr.label}</span>
        <ChevronDownIcon className="text-gray-500 w-4 h-4 flex-shrink-0" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-40 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl z-50"
          >
            {opts.map(o => (
              <button
                key={o.k}
                type="button"
                onClick={() => { onChange(o.k); setOpen(false); }}
                className={[
                  "w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2",
                  value === o.k ? "bg-blue-50/70" : ""
                ].join(" ")}
              >
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${o.dot}`} />
                <span className="text-gray-800">{o.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ========== Types ========== */
type PresenceStatus = "online" | "offline" | "away" | "dnd";
type Presence = { status: PresenceStatus; lastSeen?: string; dndUntil?: string };

type User = { _id: string; fullName: string; role?: string; presence?: Presence };
type Participant = { userId: string; fullName?: string };

type Conversation = {
  _id: string;
  type: "dm" | "group";
  title?: string;
  participants: Participant[];
  lastMessage?: { textPreview?: string; sentAt?: string; sentBy?: string; _id?: string; tombstone?: boolean };
  unreadCount?: number;
  isAI?: boolean;
  botUserId?: string;
  createdBy?: string;
  matchedMessages?: Array<{ _id: string; text: string; createdAt: string; createdBy: string }>;
};

type Receipt = { userId: string; at?: string };

type Message = {
  _id: string;
  company: string;
  conversationId: string;
  createdBy: string;
  type: "text" | "image" | "file";
  text?: string;
  createdAt?: string;
  readBy?: Receipt[];
  tombstone?: boolean;
  reactions?: Array<{ emoji: string; userId: string }>; // Multiple reactions from different users
};

type PresenceResponse = { users: User[] };

type Props = {
  company: string;
  me: { _id: string; fullName: string };
  onClose: () => void;
  isOpen: boolean;
  apiBase?: string;
  onUnreadChange?: (n: number) => void;
  openLumiraAI?: boolean;
};

/* ========== Constants ========== */
const BOT_NAME = "Lumira AI";
const TYPE_INTERVAL_MS = 14;
const TYPE_MIN_STEP = 1;
const TYPE_SEGMENTS = 90;

/* --- Footer tab --- */
const IconTab: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  title?: string;
  badge?: number;
  size?: "sm" | "md";
}> = ({ active, onClick, icon, label, title, badge, size = "sm" }) => {
  // Responsive sizing for mobile and desktop
  const wrap = size === "sm" ? "w-[60px] sm:w-[64px] py-0" : "w-[72px] sm:w-[80px] py-1";
  const circle = size === "sm" ? "w-7 h-7 sm:w-8 sm:h-8" : "w-9 h-9 sm:w-10 sm:h-10";
  const labelCls = size === "sm" ? "text-[9.5px] sm:text-[10.5px]" : "text-[10.5px] sm:text-[11.5px]";
  return (
    <button
      title={title || label}
      onClick={onClick}
      className={[
        "relative flex flex-col items-center justify-center gap-0.5 sm:gap-1 shrink-0 select-none",
        wrap,
        "focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-200 rounded-xl"
      ].join(" ")}
    >
      <div
        className={[
          "relative flex items-center justify-center rounded-full transition-all",
          circle,
          active ? "bg-blue-600 text-white shadow-md" : "bg-white text-gray-700 border border-gray-200 shadow-sm"
        ].join(" ")}
      >
        {icon}
        {!!badge && (
          <span className="absolute -top-1 -right-1 sm:-top-1.5 sm:-right-1.5 min-w-[14px] sm:min-w-[16px] h-[14px] sm:h-[16px] px-0.5 sm:px-1 rounded-full bg-green-600 text-white text-[8px] sm:text-[9px] leading-[14px] sm:leading-[16px] text-center font-semibold shadow">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <div className={["font-medium leading-tight", labelCls, active ? "text-blue-700" : "text-gray-600"].join(" ")}>
        {label}
      </div>
    </button>
  );
};

/* ========== Utils ========== */
// jfetch removed - using apiClient instead
const statusDot = (s?: PresenceStatus) =>
  ({ online: "bg-green-500", away: "bg-yellow-500", dnd: "bg-red-500", offline: "bg-gray-400" }[s || "offline"]);
const fmtHM = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const dayLabel = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  if (isSameDay(d, today)) return T.today;
  if (isSameDay(d, yest)) return T.yesterday;
  return d.toLocaleDateString("he-IL", { day: "2-digit", month: "short", year: "numeric" });
};
const avatar = (name?: string) => (name?.trim()?.[0]?.toUpperCase() || "?");

function patchAI(c: Conversation): Conversation {
  const looksAI = Boolean(c.isAI || c.botUserId || /(^|\s)(lumira\s*ai|ai\s*assistant)\b/i.test(c.title || ""));
  return looksAI ? { ...c, isAI: true, title: BOT_NAME } : c;
}
const isAIConv = (c: Conversation) => Boolean(c.isAI || c.botUserId);

/* Persist tiny state */
function usePersistedState<T>(key: string, initial: T) {
  const [val, setVal] = useState<T>(() => {
    try { const s = localStorage.getItem(key); return s ? (JSON.parse(s) as T) : initial; } catch { return initial; }
  });
  
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }, [key, val]);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => { if (e.key === key && e.newValue) { try { setVal(JSON.parse(e.newValue)); } catch {} } };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key]);
  return [val, setVal] as const;
}

/* ========== Component ========== */
export default function ChatPopup({
  company, me, onClose, isOpen, apiBase, onUnreadChange, openLumiraAI = false,
}: Props) {
  const emit = useSocketEmit();
  const isVisible = usePageVisibility(); // Pause polling when tab is hidden
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [active, setActive] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [team, setTeam] = useState<User[]>([]);
  const [userCache, setUserCache] = useState<Record<string, User>>({});
  const [draft, setDraft] = useState("");
  const [voiceLang, setVoiceLang] = useState<LangCode>('ar-PS');
  const [voiceInterim, setVoiceInterim] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  const [botTyping, setBotTyping] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchMessageId, setSearchMessageId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [hasClickedNewMessageButton, setHasClickedNewMessageButton] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [forwardingMessages, setForwardingMessages] = useState<Set<string> | null>(null);
  const [quickReactionMessageId, setQuickReactionMessageId] = useState<string | null>(null);
  const [quickReactionPosition, setQuickReactionPosition] = useState<{ x: number; y: number } | null>(null);
  const [showFullEmojiPicker, setShowFullEmojiPicker] = useState(false);
  const longPressTimerRef = useRef<number | null>(null);
  const [messageReactions, setMessageReactions] = useState<Record<string, Array<{ emoji: string; userId: string }>>>({}); // messageId -> reactions array
  const reactionClickTimesRef = useRef<Record<string, number>>({}); // messageId -> lastClickTime

  const [myStatus, setMyStatus] = usePersistedState<"online" | "dnd" | "offline">(
    `chat:status:${company}:${me._id}`, "online"
  );

  // Check if Lumira AI is hidden from sidebar
  const { hiddenFromSidebar, loading: featuresLoading, refresh: refreshFeatures } = useCompanyFeatures();

  type Pane = "list" | "new-dm" | "new-group";
  const [pane, setPane] = useState<Pane>("list");
  const [filter, setFilter] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupSel, setGroupSel] = useState<Set<string>>(new Set());

  const chatLangConfig = useMemo(() => SPEECH_LANG_CONFIG[voiceLang] ?? SPEECH_LANG_CONFIG['ar-PS'], [voiceLang]);

  // normalize for multilingual, accent-insensitive substring matching
  function normalizeForSearch(s?: string) {
    if (!s) return "";
    try {
      // NFD + remove diacritics (Unicode property) then lower-case
      return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
    } catch {
      // environment may not support \p{Diacritic}; fallback to simple lower-case
      return s.toLowerCase();
    }
  }
  
  // live-filtered conversations while typing
  const filteredConversations = useMemo(() => {
    const q = normalizeForSearch(filter).trim();
    if (!q) return conversations;
    return conversations.filter(c => {
      if (normalizeForSearch(c.title).includes(q)) return true;
      if (normalizeForSearch(c.lastMessage?.textPreview).includes(q)) return true;
      return (c.participants || []).some(p => normalizeForSearch(p.fullName).includes(q));
    });
  }, [conversations, filter]);

  const [typingIds, setTypingIds] = useState<Set<string>>(new Set());
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingSent = useRef(false);

  const tempIds = useRef<Set<string>>(new Set());
  const typeTimers = useRef<Record<string, number>>({});
  const [typedTextById, setTypedTextById] = useState<Record<string, string>>({});

  const [typingByConv, setTypingByConv] = useState<Record<string, Set<string>>>({});
  const typingListTimers = useRef<Record<string, Record<string, number>>>({});
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const forwardingMessageTextsRef = useRef<string>("");
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const allConversationsRef = useRef<Conversation[]>([]);
  const currentSearchRef = useRef<string>("");
  const messagesCacheRef = useRef<Record<string, Message[]>>({});

  // Scroll to bottom function (optimized for performance)
  const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
    if (bottomRef.current && messagesContainerRef.current) {
      // Use direct scroll instead of scrollIntoView for better performance
      if (behavior === "auto") {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      } else {
        bottomRef.current.scrollIntoView({ behavior, block: "end" });
      }
    }
  };

  // Check if user is near bottom of scroll container
  const isNearBottom = (container: HTMLElement, threshold: number = 100): boolean => {
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight < threshold;
  };

  // Handle scroll events to detect if user manually scrolled up (throttled for performance)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const nearBottom = isNearBottom(container);
          setIsUserScrolledUp(!nearBottom);
          // If user scrolls back to bottom, enable auto-scroll again and hide new message button
          if (nearBottom) {
            setShouldAutoScroll(true);
            setHasNewMessages(false);
            setHasClickedNewMessageButton(false); // Reset when user scrolls back to bottom
          } else {
            // User scrolled up - reset the click flag so button can appear again if new message arrives
            setHasClickedNewMessageButton(false);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [active?._id]); // Re-attach when conversation changes

  /* NEW: group members panel + "seen by" popover state */
  const [openSeenFor, setOpenSeenFor] = useState<string | null>(null);

  // --- Add Members state ---
  const [showMembers, setShowMembers] = useState(false);           // you already have this if you used my last version
  const [addMode, setAddMode] = useState(false);
  const [addSel, setAddSel] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null); // Track which member is being removed

  const inActiveGroup = (uid: string) =>
    Boolean(active?.type === "group" && active?.participants?.some(p => p.userId === uid));

  const addCandidates = useMemo(
    () =>
      active?.type === "group"
        ? team.filter(u => u._id !== me._id && !inActiveGroup(u._id))
        : [],
    [team, active?.type, active?._id, active?.participants, me._id]
  );

  async function addMembersToActiveGroup(ids: string[]) {
    if (!active || active.type !== "group" || ids.length === 0) return;
    setAdding(true);
    try {
      // PATCH your backend (see server snippet below)
      const updated = await updateConversationParticipants(company, active._id, ids);

      const patched = patchAI(updated);
      
      // Ensure participants have fullName from team or userCache
      const enrichedParticipants = patched.participants.map(p => {
        const teamUser = team.find(t => t._id === p.userId);
        const cachedUser = userCache[p.userId];
        return {
          ...p,
          fullName: p.fullName || cachedUser?.fullName || teamUser?.fullName || ""
        };
      });
      
      // Update userCache with new participants
      setUserCache(prev => {
        const updated = { ...prev };
        for (const p of enrichedParticipants) {
          if (!updated[p.userId]) {
            // Find in team list for presence data
            const teamUser = team.find(t => t._id === p.userId);
            updated[p.userId] = {
              _id: p.userId,
              fullName: p.fullName || teamUser?.fullName || "",
              role: teamUser?.role,
              presence: teamUser?.presence || { status: "offline" }
            };
          }
        }
        return updated;
      });
      
      // Create a new active object with enriched participants to ensure React detects the change
      const updatedActive = {
        ...patched,
        participants: enrichedParticipants
      };
      
      setActive(updatedActive);
      setConversations(prev => [updatedActive, ...prev.filter(c => c._id !== updatedActive._id)]);
      setAddSel(new Set());
      setAddMode(false);
      
      // Request presence for newly added members
      if (ids.length > 0) {
        emit("presence:request", { userIds: ids });
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setAdding(false);
    }
  }

  async function removeMemberFromGroup(userId: string) {
    if (!active || active.type !== "group" || !userId) return;
    setRemoving(userId);
    try {
      const updated = await removeParticipantFromConversation(company, active._id, userId);

      const patched = patchAI(updated);
      
      // Ensure participants have fullName from team or userCache
      const enrichedParticipants = patched.participants.map(p => {
        const teamUser = team.find(t => t._id === p.userId);
        const cachedUser = userCache[p.userId];
        return {
          ...p,
          fullName: p.fullName || cachedUser?.fullName || teamUser?.fullName || ""
        };
      });
      
      // Create a new active object with enriched participants to ensure React detects the change
      const updatedActive = {
        ...patched,
        participants: enrichedParticipants
      };
      
      // Immediately update active state to reflect the change
      setActive(updatedActive);
      setConversations(prev => [updatedActive, ...prev.filter(c => c._id !== updatedActive._id)]);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setRemoving(null);
    }
  }
  
  const API_BASE = (
    apiBase ?? (process.env.REACT_APP_API_URL as string | undefined) ?? window.location.origin
  ).replace(/\/$/, "");

  //get a user's display name within a conversation (for group typing text)
  function nameInConv(conv: Conversation, uid: string) {
    return (
      userCache[uid]?.fullName ||
      conv.participants.find(p => p.userId === uid)?.fullName ||
      "Someone"
    );
  }

  // keep isOpen readable in callbacks without stale closures
  const isOpenRef = useRef(isOpen);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  // track last-read timestamp per conversation (client-side)
  const lastReadAtRef = useRef<Record<string, string>>({});
  // debounce per conversation for read sync
  const readDebounce = useRef<Record<string, number>>({});

  /* ===== READ MARK HELPERS ===== */
  function markRead(conversationId: string, upToId: string, upToAt?: string) {
    if (upToAt) lastReadAtRef.current[conversationId] = upToAt;
    // zero locally right away
    setConversations(prev =>
      prev.map(c => (c._id === conversationId ? { ...c, unreadCount: 0 } : c))
    );
    // notify server -> should broadcast 'messages:read' with {conversationId, by, upTo, at}
    markMessagesRead(company, conversationId, upToId).catch(() => {});
  }

  function markReadDebounced(conversationId: string, upToId: string, upToAt?: string) {
    window.clearTimeout(readDebounce.current[conversationId]);
    readDebounce.current[conversationId] = window.setTimeout(
      () => markRead(conversationId, upToId, upToAt),
      120
    );
  }

  /* Listen for feature updates */
  useEffect(() => {
    if (!company) return;
    const handleUpdate = () => {
      refreshFeatures();
    };
    window.addEventListener('company-features-updated', handleUpdate);
    return () => window.removeEventListener('company-features-updated', handleUpdate);
  }, [company, refreshFeatures]);

  /* initial conversations (outside badge visible even when closed) */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await getConversations(company);
        if (!alive) return;
        const patched = (Array.isArray(list) ? list.map(patchAI) : []).map((c: any) => ({
          ...c,
          lastForMe: c.lastForMe || c.lastMessage || null,
        }));
        setConversations(patched);
        allConversationsRef.current = patched;
      } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    })();
    return () => { alive = false; };
  }, [API_BASE, company]);

  /* open popup => refresh convs + roster (no unread zeroing here!) */
  useEffect(() => {
    if (!isOpen) return;
    setPane("list");
    setLoading(true);
    Promise.all([
      getConversations(company),
      getPresence(company) as Promise<PresenceResponse>,
    ])
      .then(([list, presence]) => {
        const normalized = (Array.isArray(list) ? list.map(patchAI) : []).map((c: any) => ({
          ...c,
          lastForMe: c.lastForMe || c.lastMessage || null,
        }));
        setConversations(normalized);
        allConversationsRef.current = normalized;

        const users = presence?.users ?? [];
        setTeam(users);
        const cache: Record<string, User> = {};
        // Add all users from presence API with their presence data
        for (const u of users) {
          cache[u._id] = {
            ...u,
            presence: u.presence || { status: "offline" }
          };
        }
        // Add "me" to cache with current status
        cache[me._id] = { 
          _id: me._id, 
          fullName: me.fullName,
          presence: { status: myStatus }
        };
        setUserCache(cache);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [API_BASE, company, isOpen, me._id, me.fullName, myStatus]);

  /* Auto-open Lumira AI if requested */
  useEffect(() => {
    if (!isOpen || !openLumiraAI || loading) return;
    
    let mounted = true;
    
    // Small delay to ensure conversations are loaded and functions are defined
    const timer = setTimeout(async () => {
      try {
        const API_BASE_LOCAL = (apiBase || (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, ""));
        const ai = await createAIConversation(company) as Conversation;
        if (!mounted) return;
        const pretty = patchAI({ ...ai, isAI: true, title: BOT_NAME });
        setConversations(prev => [pretty, ...prev.filter(c => c._id !== ai._id)]);
        // Open the conversation using the same logic as openConversation
        const patched = patchAI(pretty);
        setActive(patched);
        setPane("list");
        setSelectMode(false);
        setSelectedIds(new Set());
        setTypedTextById({});
        Object.values(typeTimers.current).forEach(t => clearTimeout(t));
        typeTimers.current = {};
        setDraft("");
        setFilter("");
        setSearchMessageId(null);
        emit("join:conversation", { conversationId: patched._id });
        
        // Load messages for the AI conversation
        try {
          const msgs = await getMessages(company, patched._id);
          if (!mounted) return;
          setMessages(msgs || []);
          markReadDebounced(patched._id, msgs?.[msgs.length - 1]?._id || "", msgs?.[msgs.length - 1]?.createdAt);
        } catch (e) {
          if (mounted) setErr(e instanceof Error ? e.message : String(e));
        }
      } catch (e) {
        if (mounted) setErr(e instanceof Error ? e.message : String(e));
      }
    }, 500);
    
    return () => {
      clearTimeout(timer);
      mounted = false;
    };
  }, [isOpen, openLumiraAI, loading, company, apiBase]);

  /* periodic refresh of conversations as fallback if socket events fail - pause when tab is hidden */
  const wasVisibleRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    
    const wasVisible = wasVisibleRef.current;
    const justBecameVisible = wasVisible === false && isVisible === true;
    wasVisibleRef.current = isVisible;
    
    // Pause refresh when tab is hidden
    if (!isVisible) {
      return; // Don't set up interval when tab is hidden
    }
    
    // If tab just became visible, trigger a single refresh
    if (justBecameVisible) {
      // Trigger immediate refresh when tab becomes visible
      (async () => {
        try {
          const list = await getConversations(company);
          const normalized = (Array.isArray(list) ? list.map(patchAI) : []).map((c: any) => ({
            ...c,
            lastForMe: c.lastForMe || c.lastMessage || null,
          }));
          
          setConversations(prev => {
            const prevIds = new Set(prev.map(c => c._id));
            const newIds = new Set(normalized.map(c => c._id));
            
            const hasNew = normalized.some(c => !prevIds.has(c._id));
            
            if (hasNew) {
              const existing = prev.filter(c => newIds.has(c._id));
              const newOnes = normalized.filter(c => !prevIds.has(c._id));
              return [...newOnes, ...existing];
            }
            
            return normalized.map(newConv => {
              const existing = prev.find(c => c._id === newConv._id);
              if (existing && active?._id !== newConv._id) {
                return { ...newConv, unreadCount: existing.unreadCount };
              }
              return newConv;
            });
          });
          
          allConversationsRef.current = normalized;
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.error("Periodic conversation refresh failed:", e);
          }
        }
      })();
    }
    
    // Refresh every 60 seconds to catch new groups/conversations (only when tab is visible)
    const interval = setInterval(async () => {
      if (!isVisible) return; // Don't run if tab became hidden
      
      try {
        const list = await getConversations(company);
        const normalized = (Array.isArray(list) ? list.map(patchAI) : []).map((c: any) => ({
          ...c,
          lastForMe: c.lastForMe || c.lastMessage || null,
        }));
        
        // Only update if we have new conversations (to avoid disrupting user experience)
        setConversations(prev => {
          const prevIds = new Set(prev.map(c => c._id));
          const newIds = new Set(normalized.map(c => c._id));
          
          // Check if there are any new conversations
          const hasNew = normalized.some(c => !prevIds.has(c._id));
          
          if (hasNew) {
            // Merge: keep existing order for old ones, add new ones at the top
            const existing = prev.filter(c => newIds.has(c._id));
            const newOnes = normalized.filter(c => !prevIds.has(c._id));
            return [...newOnes, ...existing];
          }
          
          // No new conversations, but update existing ones with latest data
          return normalized.map(newConv => {
            const existing = prev.find(c => c._id === newConv._id);
            // Preserve unread count if we're not actively viewing it
            if (existing && active?._id !== newConv._id) {
              return { ...newConv, unreadCount: existing.unreadCount };
            }
            return newConv;
          });
        });
        
        allConversationsRef.current = normalized;
      } catch (e) {
        // Silently fail - don't disrupt user experience
        if (process.env.NODE_ENV === 'development') {
          console.error("Periodic conversation refresh failed:", e);
        }
      }
    }, 60000); // Increased from 15s to 60s to reduce CPU/memory usage
    
    return () => clearInterval(interval);
  }, [API_BASE, company, isOpen, active?._id, isVisible]);

  // Track if we're currently setting status to prevent circular updates
  const settingStatusRef = useRef(false);

  /* push myStatus to server whenever it changes */
  useEffect(() => {
    // Skip initial mount to avoid unnecessary emission
    if (!isOpen) return;
    
    const statusPayload = myStatus === "offline" ? "invisible" : myStatus;
    settingStatusRef.current = true;
    // Explicitly send with our userId to ensure server knows it's for us
    emit("presence:set", { 
      status: statusPayload, 
      userId: String(me._id) // Ensure it's a string
    });
    // Reset flag after a short delay to allow server response
    setTimeout(() => {
      settingStatusRef.current = false;
    }, 500);
  }, [emit, myStatus, me._id, isOpen]);

  /* reflect presence updates for all users */
  useEffect(() => {
    const onPresence = ({ userId, status }: { userId: string; status: Presence }) => {
      // Ensure userId is a valid string
      if (!userId || typeof userId !== 'string') {
        console.warn('Invalid userId in presence:update', { userId, status });
        return;
      }
      
      // Handle "invisible" status from server (convert to "offline")
      // Server may send "invisible" which is not in PresenceStatus type, so we check as string first
      const rawStatus = (status?.status as string | undefined) || "online";
      const s = (rawStatus === "invisible" || rawStatus === "offline" ? "offline" : 
                 rawStatus === "dnd" ? "dnd" : 
                 rawStatus === "away" ? "away" : 
                 "online") as PresenceStatus;
      // Normalize both IDs to strings and trim whitespace for comparison
      const userIdStr = String(userId).trim();
      const meIdStr = String(me._id).trim();
      
      // Only update myStatus from server if:
      // 1. It's actually me (strict string comparison after normalization)
      // 2. We're not currently setting the status ourselves (to prevent circular updates)
      if (userIdStr === meIdStr) {
        // This is my own status update from the server
        if (!settingStatusRef.current && (s === "dnd" || s === "offline" || s === "online")) {
          setMyStatus(s as "online" | "dnd" | "offline");
        }
        // Don't update userCache for ourselves - myStatus is the source of truth
        // This prevents accidentally updating our own status in the cache when we change it
        return;
      }
      
      // Update userCache for OTHER users only (not ourselves)
      // This ensures we never accidentally update the other person's status when we change our own
      setUserCache(prev => {
        const updated = { ...prev };
        // Normalize status: "invisible" becomes "offline" for display
        const normalizedStatus = s;
        
        if (updated[userIdStr]) {
          // Preserve existing user data, only update presence
          updated[userIdStr] = {
            ...updated[userIdStr],
            presence: {
              status: normalizedStatus,
              lastSeen: status?.lastSeen,
              dndUntil: status?.dndUntil,
            }
          };
        } else {
          // If user not in cache yet, try to find them in team list first
          const teamUser = team.find(t => String(t._id).trim() === userIdStr);
          updated[userIdStr] = {
            _id: userIdStr,
            fullName: teamUser?.fullName || "",
            role: teamUser?.role,
            presence: {
              status: normalizedStatus,
              lastSeen: status?.lastSeen,
              dndUntil: status?.dndUntil,
            }
          };
        }
        return updated;
      });
    };
    const unsubPresence = socketManager.subscribe("presence:update", onPresence);
    return () => { unsubPresence(); };
  }, [me._id, setMyStatus, team]);

  /* fetch presence for all participants in active conversation (including admin) */
  useEffect(() => {
    if (!active || active.type !== "group") return;
    
    const fetchParticipantsPresence = async () => {
      const participantIds = active.participants
        .map(p => p.userId)
        .filter(id => id !== me._id && !userCache[id]?.presence);
      
      if (participantIds.length === 0) return;
      
      try {
        // Fetch presence for participants not in cache
        const presenceResponse = await getPresence(company) as PresenceResponse;
        const allUsers = presenceResponse?.users ?? [];
        
        // Also check if we can get presence for specific users
        // For now, update cache with any users we find
        setUserCache(prev => {
          const updated = { ...prev };
          for (const user of allUsers) {
            if (participantIds.includes(user._id)) {
              updated[user._id] = {
                ...updated[user._id],
                ...user,
                presence: user.presence || { status: "offline" }
              };
            }
          }
          return updated;
        });
      } catch (err) {
        console.error("Failed to fetch participants presence", err);
      }
    };
    
    fetchParticipantsPresence();
  }, [active?._id, active?.participants, userCache, API_BASE, company, me._id]);

  /* perform database search when filter changes */
  useEffect(() => {
    const performSearch = async () => {
      const q = filter.trim();
      if (!q || q.length < 2) {
        // If search is cleared, restore original conversations
        currentSearchRef.current = "";
        if (allConversationsRef.current.length > 0) {
          setConversations(allConversationsRef.current);
        }
        return;
      }

      setSearchLoading(true);
      try {
        const results = await searchConversations(company, q);
        if (results && Array.isArray(results)) {
          const patched = results.map(patchAI).map((c: any) => ({
            ...c,
            lastForMe: c.lastForMe || c.lastMessage || null,
          }));
          setConversations(patched);
          currentSearchRef.current = q;
        }
      } catch (err) {
        console.error("Search failed", err);
      } finally {
        setSearchLoading(false);
      }
    };

    const timeoutId = setTimeout(performSearch, 300); // Debounce
    return () => clearTimeout(timeoutId);
  }, [filter, API_BASE, company]);

  /* restore search results when coming back from a conversation */
  useEffect(() => {
    if (!active && currentSearchRef.current && filter.trim() === currentSearchRef.current) {
      // We're back on the list, and still searching. Re-run search to restore results.
      const performSearch = async () => {
        setSearchLoading(true);
        try {
          const results = await searchConversations(company, currentSearchRef.current) as Conversation[];
          if (results && Array.isArray(results)) {
            const patched = results.map(patchAI).map((c: any) => ({
              ...c,
              lastForMe: c.lastForMe || c.lastMessage || null,
            }));
            setConversations(patched);
          }
        } catch (err) {
          console.error("Search restore failed", err);
        } finally {
          setSearchLoading(false);
        }
      };
      performSearch();
    }
  }, [active, API_BASE, company]);

  /* preview helper */
  function computeLastPreview(arr: Message[] | undefined) {
    if (!arr || !arr.length) return undefined;
    const last = [...arr].filter(m => !m.tombstone).slice(-1)[0];
    if (!last) return undefined;
    return {
      _id: last._id,
      textPreview: (last.text || "").slice(0, 120),
      sentAt: last.createdAt,
      sentBy: last.createdBy,
      tombstone: false,
    };
  }

  // helper: should a message increment unread?
  function shouldCountUnread(message: Message, convId: string, isActive: boolean) {
    if (!message) return false;
    const isMine = String(message.createdBy) === me._id;
    if (isMine) return false;
    if (isActive) return false;

    // if server says you've read it, don't count
    const alreadyRead = Array.isArray(message.readBy) && message.readBy.some(r => String(r.userId) === me._id);
    if (alreadyRead) return false;

    // if it's older/equal to our last-read, don't count
    const lastReadIso = lastReadAtRef.current[convId];
    if (lastReadIso && message.createdAt) {
      const msgT = new Date(message.createdAt).getTime();
      const lastT = new Date(lastReadIso).getTime();
      if (msgT <= lastT) return false;
    }
    return true;
  }

  /* sockets */
  useEffect(() => {
    const onMsg = ({ conversationId, message }: { conversationId: string; message: Message }) => {
      const isMine = String(message.createdBy) === me._id;
      const isInActive = !!(active && active._id === conversationId);

      // If we're inside that conversation -> smooth append, no refetch
      if (isInActive) {
        if (active?.botUserId && message.createdBy === active.botUserId) {
          setBotTyping(false);
          startTypewriter(message);
        }
        if (isMine && tempIds.current.size) {
          setMessages(prev => prev.filter(m => !tempIds.current.has(m._id) && m.text !== message.text));
          tempIds.current.clear();
        }
        setMessages(prev => {
          if (prev.some(m => m._id === message._id)) return prev;
          const out = [...prev, message].sort(
            (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
          );
          
          // Update reactions if message has one
          if ((message as any).reaction) {
            setMessageReactions(prevReactions => ({
              ...prevReactions,
              [message._id]: (message as any).reaction
            }));
          }
          
          // Update cache
          if (active?._id) {
            messagesCacheRef.current[active._id] = out;
          }
          return out;
        });
        // If user is scrolled up and new message arrives, show button (don't auto-scroll)
        // Check isUserScrolledUp state directly (not ref) to ensure it's current
        const isCurrentlyScrolledUp = messagesContainerRef.current 
          ? !isNearBottom(messagesContainerRef.current)
          : isUserScrolledUp;
        
        if (isCurrentlyScrolledUp && !isMine && !hasClickedNewMessageButton) {
          // Show "new message" button - don't auto-scroll
          setHasNewMessages(true);
          setShouldAutoScroll(false); // Prevent auto-scroll when user is scrolled up
        } else if (!isCurrentlyScrolledUp || isMine) {
          // User is at bottom or it's their own message - auto-scroll
          setShouldAutoScroll(true); // Allow auto-scroll
          setTimeout(() => scrollToBottom("auto"), 0);
          setHasNewMessages(false);
          setHasClickedNewMessageButton(false);
        }

        // When active and it's from someone else, mark read immediately so the sender sees ✓✓
        if (!isMine) {
          const upToId = message._id;
          markReadDebounced(conversationId, upToId, message.createdAt);
        }

        // Optimistically advance lastReadAt
        if (message.createdAt) {
          lastReadAtRef.current[conversationId] = message.createdAt;
        }
      }

      // Update cache for this conversation (even if not active) - CRITICAL for message persistence
      if (messagesCacheRef.current[conversationId]) {
        const cached = messagesCacheRef.current[conversationId];
        // Check if message already exists in cache (avoid duplicates)
        if (!cached.some((m: any) => m._id === message._id)) {
          const updated = [...cached, message].sort(
            (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
          );
          messagesCacheRef.current[conversationId] = updated;
        }
      } else {
        // If no cache exists, create it with this message
        messagesCacheRef.current[conversationId] = [message];
      }

      // Update conv list preview & unread (keep lastForMe in sync!)
      setConversations(prev => {
        const idx = prev.findIndex(c => c._id === conversationId);
        const updatedLast = {
          _id: message._id,
          textPreview: (message.text || "").slice(0, 120),
          sentAt: message.createdAt,
          sentBy: message.createdBy,
        };

        if (idx === -1) {
          const count = shouldCountUnread(message, conversationId, isInActive) ? 1 : 0;
          const stub: any = patchAI({
            _id: conversationId,
            type: "dm",
            participants: [{ userId: message.createdBy }],
            lastMessage: updatedLast,
            unreadCount: count,
          } as Conversation);
          (stub as any).lastForMe = updatedLast;
          return [stub, ...prev];
        }

        const c = prev[idx];
        const inc = shouldCountUnread(message, conversationId, isInActive) ? 1 : 0;

        const next = [...prev];
        const patched: any = patchAI({
          ...c,
          lastMessage: updatedLast,
          unreadCount: isInActive ? 0 : (c.unreadCount || 0) + inc
        });
        (patched as any).lastForMe = updatedLast;
        next[idx] = patched;

        // move updated conv to top
        const [moved] = next.splice(idx, 1);
        return [moved, ...next];
      });
    };

    const onConv = (c: Conversation) => {
      const patched = patchAI(c);
      const isParticipant = patched.participants?.some(p => String(p.userId) === String(me._id));
      
      if (isParticipant) {
        // Enrich participants with fullName
        const enrichedParticipants = patched.participants.map(p => {
          const teamUser = team.find(t => t._id === p.userId);
          const cachedUser = userCache[p.userId];
          return {
            ...p,
            fullName: p.fullName || cachedUser?.fullName || teamUser?.fullName || ""
          };
        });
        const enriched = {
          ...patched,
          participants: enrichedParticipants
        };
        
        setConversations(prev => {
          const copy = prev.filter(x => x._id !== enriched._id);
          const existing = prev.find(x => x._id === enriched._id);
          // Preserve "הגיב באימוגי" if it exists - don't override with backend's lastMessage
          const shouldPreserveReaction = existing?.lastMessage?.textPreview?.startsWith('הגיב באימוגי') || existing?.lastMessage?.textPreview?.startsWith('הגיב באימוגי');
          const withPreview: any = { 
            ...enriched,
            // If existing has "הגיב באימוגי", keep it - don't override with backend's lastMessage
            lastMessage: shouldPreserveReaction && existing ? existing.lastMessage : (enriched.lastMessage || existing?.lastMessage),
            lastForMe: shouldPreserveReaction && existing ? (existing as any).lastForMe : ((enriched as any).lastForMe || enriched.lastMessage || (existing as any)?.lastForMe || null)
          };
          return [withPreview, ...copy];
        });
        
        // Ensure all participants are in userCache (for presence display)
        if (enriched.type === "group" && enriched.participants) {
          setUserCache(prev => {
            const updated = { ...prev };
            for (const p of enriched.participants) {
              if (!updated[p.userId]) {
                const teamUser = team.find(t => t._id === p.userId);
                updated[p.userId] = {
                  _id: p.userId,
                  fullName: p.fullName || teamUser?.fullName || "",
                  role: teamUser?.role,
                  presence: teamUser?.presence || { status: "offline" }
                };
              }
            }
            return updated;
          });
          
          // Request presence for all participants
          const participantIds = enriched.participants
            .map(p => p.userId)
            .filter(id => id !== me._id);
          if (participantIds.length > 0) {
            emit("presence:request", { userIds: participantIds });
          }
        }
      }
    };

    // >>> Only honor 'messages:read' if 'by' exists. Zero unread only for my own reads.
    const onRead = ({ conversationId, by, upTo, at }: { conversationId: string; by?: string; upTo?: string; at?: string }) => {
      if (!by) return;

      if (String(by) === String(me._id)) {
        if (at) lastReadAtRef.current[conversationId] = at;
        else lastReadAtRef.current[conversationId] = new Date().toISOString();

        setConversations(prev =>
          prev.map(c => (c._id === conversationId ? { ...c, unreadCount: 0 } : c))
        );
      }

      // Update message read receipts in the open thread
      setMessages(prev => {
        const inThis = active && active._id === conversationId;
        if (!inThis || !prev.length) return prev;

        const idxUpTo = upTo ? prev.findIndex(m => m._id === upTo) : prev.length - 1;
        const limit = idxUpTo === -1 ? prev.length - 1 : idxUpTo;

        let changed = false;
        const out = prev.map((m, i) => {
          if (i > limit) return m;
          const had = (m.readBy || []).some(r => String(r.userId) === String(by));
          if (had) return m;
          changed = true;
          return { ...m, readBy: [...(m.readBy || []), { userId: by, at }] };
        });
        return changed ? out : prev;
      });
    };

    const onDeleted = ({
      conversationId, messageId, by, tombstone,
    }: { conversationId: string; messageId: string; by: string; tombstone?: boolean }) => {
      let nextMsgs: Message[] | undefined;

      if (active && active._id === conversationId) {
        setMessages(prev => {
          let out = prev;
          if (by === me._id && !tombstone) out = prev.filter(m => m._id !== messageId);
          else if (tombstone)
            out = prev.map(m => (m._id === messageId ? { ...m, tombstone: true, text: undefined } : m));
          nextMsgs = out;
          return out;
        });
      }

      setConversations(prev =>
        prev.map(c => {
          if (c._id !== conversationId) return c;
          const wasPreview = c.lastMessage?._id === messageId || (c as any).lastForMe?._id === messageId;
          if (!wasPreview) return c;
          const newLast = computeLastPreview(nextMsgs);
          const patched: any = { ...c, lastMessage: newLast };
          (patched as any).lastForMe = newLast;
          return patched;
        })
      );
    };

    const onTyping = (payload: { conversationId: string; userId: string; isTyping: boolean }) => {
      const { conversationId, userId, isTyping } = payload;
      if (String(userId) === String(me._id)) return; // ignore ourselves

      // --- Always update typing map used by the list (even if not active)
      setTypingByConv(prev => {
        const next = { ...prev };
        const set = new Set(next[conversationId] || []);
        if (isTyping) set.add(userId); else set.delete(userId);
        if (set.size) next[conversationId] = set; else delete next[conversationId];
        return next;
      });

      // refresh per-user expiry (auto clear after 4s)
      typingListTimers.current[conversationId] ||= {};
      clearTimeout(typingListTimers.current[conversationId][userId]);
      if (isTyping) {
        typingListTimers.current[conversationId][userId] = window.setTimeout(() => {
          setTypingByConv(prev => {
            const next = { ...prev };
            const set = new Set(next[conversationId] || []);
            set.delete(userId);
            if (set.size) next[conversationId] = set; else delete next[conversationId];
            return next;
          });
        }, 4000);
      }

      // --- Keep existing in-thread typing indicators for the active convo
      if (!active || conversationId !== active._id) return;

      const isBot = active.botUserId && userId === active.botUserId;
      if (isBot) setBotTyping(!!isTyping);

      setTypingIds(prev => {
        const next = new Set(prev);
        if (isTyping) {
          next.add(userId);
        } else {
          next.delete(userId);
        }
        return next;
      });

      // Auto-clear typing indicator after 4 seconds
      if (isTyping) {
        const timeoutId = window.setTimeout(() => {
          setTypingIds(prev => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          });
        }, 4000);
        // Store timeout to clear it if needed
        typingListTimers.current[conversationId] = typingListTimers.current[conversationId] || {};
        typingListTimers.current[conversationId][userId] = timeoutId as any;
      }
    };

    const onConvUpdated = (updated: Conversation) => {
      const patched = patchAI(updated);
      
      // Check if current user is a participant (e.g., was just added to a group)
      const isParticipant = patched.participants?.some(p => String(p.userId) === String(me._id));
      
      // If this is the active conversation, update it immediately
      if (active && active._id === patched._id) {
        // Enrich participants with fullName
        const enrichedParticipants = patched.participants.map(p => {
          const teamUser = team.find(t => t._id === p.userId);
          const cachedUser = userCache[p.userId];
          return {
            ...p,
            fullName: p.fullName || cachedUser?.fullName || teamUser?.fullName || ""
          };
        });
        const updatedActive = {
          ...patched,
          participants: enrichedParticipants
        };
        setActive(updatedActive);
      }
      
      // Update in conversations list, or add if it doesn't exist and user is a participant
      setConversations(prev => {
        const idx = prev.findIndex(c => c._id === patched._id);
        
        // Preserve "הגיב באימוגי" if it exists - don't override with backend's lastMessage
        const existingConv = idx >= 0 ? prev[idx] : null;
        const shouldPreserveReaction = existingConv?.lastMessage?.textPreview?.startsWith('הגיב באימוגי');
        
        // Enrich participants with fullName
        const enrichedParticipants = patched.participants.map(p => {
          const teamUser = team.find(t => t._id === p.userId);
          const cachedUser = userCache[p.userId];
          return {
            ...p,
            fullName: p.fullName || cachedUser?.fullName || teamUser?.fullName || ""
          };
        });
        const enriched = {
          ...patched,
          participants: enrichedParticipants
        };
        
        // If conversation doesn't exist and user is a participant, add it
        if (idx === -1) {
          if (isParticipant) {
            const withPreview: any = { 
              ...enriched, 
              lastForMe: (enriched as any).lastForMe || enriched.lastMessage || null 
            };
            return [withPreview, ...prev];
          }
          return prev;
        }
        
        // Update existing conversation
        // CRITICAL: Preserve "הגיב באימוגי" if it exists - don't override with backend's lastMessage
        const next = [...prev];
        const existing = prev[idx];
        const withPreview: any = { 
          ...enriched,
          // If existing has "הגיב באימוגי", keep it - don't override with backend's lastMessage
          lastMessage: shouldPreserveReaction && existing ? existing.lastMessage : (enriched.lastMessage || existing?.lastMessage),
          lastForMe: shouldPreserveReaction && existing ? (existing as any).lastForMe : ((enriched as any).lastForMe || enriched.lastMessage || (existing as any)?.lastForMe || null)
        };
        next[idx] = withPreview;
        return next;
      });
      
      // If user was just added to a group, ensure participants are in userCache
      if (isParticipant && patched.type === "group" && patched.participants) {
        setUserCache(prev => {
          const updated = { ...prev };
          for (const p of patched.participants) {
            if (!updated[p.userId]) {
              const teamUser = team.find(t => t._id === p.userId);
              updated[p.userId] = {
                _id: p.userId,
                fullName: p.fullName || teamUser?.fullName || "",
                role: teamUser?.role,
                presence: teamUser?.presence || { status: "offline" }
              };
            }
          }
          return updated;
        });
        
        // Request presence for all participants
        const participantIds = patched.participants
          .map(p => p.userId)
          .filter(id => id !== me._id);
        if (participantIds.length > 0) {
          emit("presence:request", { userIds: participantIds });
        }
      }
    };

    const onReaction = ({ messageId, emoji, userId, conversationId }: { messageId: string; emoji: string; userId?: string; conversationId?: string }) => {
      if (!userId) {
        console.warn('[ChatPopup] onReaction: No userId, returning');
        return;
      }
      
      // Find the message to get conversationId - use conversationId from payload if available
      let targetConversationId: string | null = conversationId || null;
      let targetMessage: Message | null = null;
      
      // If conversationId not in payload, find it from multiple sources
      if (!targetConversationId) {
        // Check in active messages first (fastest)
        if (active && messages.length > 0) {
          const msg = messages.find(m => m._id === messageId);
          if (msg) {
            targetConversationId = msg.conversationId;
            targetMessage = msg;
          }
        }
        
        // If not found, check in conversations cache
        if (!targetConversationId) {
          for (const conv of conversations) {
            if (conv.lastMessage?._id === messageId) {
              targetConversationId = conv._id;
              break;
            }
          }
        }
        
        // If still not found, check in messages cache
        if (!targetConversationId) {
          for (const [convId, cachedMsgs] of Object.entries(messagesCacheRef.current)) {
            const msg = cachedMsgs.find((m: any) => m._id === messageId);
            if (msg) {
              targetConversationId = convId;
              targetMessage = msg;
              break;
            }
          }
        }
      }
      
      // Update reactions array for this message - IMMEDIATE update for fast UI
      // CRITICAL: Only update the reaction for the specific userId, never replace all reactions
      setMessageReactions(prev => {
        const currentReactions: Array<{ emoji: string; userId: string }> = (prev[messageId] || []) as Array<{ emoji: string; userId: string }>;
        // Check if THIS SPECIFIC USER already has a reaction (not any user)
        const existingIndex = currentReactions.findIndex((r: { emoji: string; userId: string }) => {
          return String(r.userId) === String(userId);
        });
        
        let updatedReactions: Array<{ emoji: string; userId: string }>;
        if (existingIndex >= 0) {
          // Update ONLY this user's existing reaction (changing emoji for this user only)
          updatedReactions = [...currentReactions];
          updatedReactions[existingIndex] = { emoji, userId: String(userId) };
        } else {
          // Add new reaction for this user (keep all other users' reactions)
          updatedReactions = [...currentReactions, { emoji, userId: String(userId) }];
        }
        
        return {
          ...prev,
          [messageId]: updatedReactions
        };
      });
      
      // Update reactions in cached messages if present
      // CRITICAL: Only update the reaction for the specific userId
      if (targetConversationId && messagesCacheRef.current[targetConversationId]) {
        const cached = messagesCacheRef.current[targetConversationId];
        const msgIndex = cached.findIndex((m: any) => m._id === messageId);
        if (msgIndex >= 0) {
          const msg = cached[msgIndex];
          const currentReactions = msg.reactions || [];
          // Find THIS SPECIFIC USER's reaction only
          const existingIndex = currentReactions.findIndex((r: any) => {
            return String(r.userId) === String(userId);
          });
          
          let updatedReactions: any[];
          if (existingIndex >= 0) {
            // Update ONLY this user's reaction, keep all others
            updatedReactions = [...currentReactions];
            updatedReactions[existingIndex] = { emoji, userId: String(userId) };
          } else {
            // Add new reaction for this user, keep all existing reactions
            updatedReactions = [...currentReactions, { emoji, userId: String(userId) }];
          }
          
          const updated = [...cached];
          updated[msgIndex] = { 
            ...updated[msgIndex], 
            reactions: updatedReactions
          };
          messagesCacheRef.current[targetConversationId] = updated;
        }
      }
      
      // Update reactions in current messages if this conversation is active
      // CRITICAL: Only update the reaction for the specific userId
      if (active && active._id === targetConversationId) {
        setMessages(prev => {
          const msgIndex = prev.findIndex((m: any) => m._id === messageId);
          if (msgIndex >= 0) {
            const msg = prev[msgIndex];
            const currentReactions = msg.reactions || [];
            // Find THIS SPECIFIC USER's reaction only
            const existingIndex = currentReactions.findIndex((r: any) => {
              return String(r.userId) === String(userId);
            });
            
            let updatedReactions: any[];
            if (existingIndex >= 0) {
              // Update ONLY this user's reaction, keep all others unchanged
              updatedReactions = [...currentReactions];
              updatedReactions[existingIndex] = { emoji, userId: String(userId) };
            } else {
              // Add new reaction for this user, keep all existing reactions
              updatedReactions = [...currentReactions, { emoji, userId: String(userId) }];
            }
            
            const updated = [...prev];
            updated[msgIndex] = { 
              ...updated[msgIndex], 
              reactions: updatedReactions
            };
            return updated;
          }
          return prev;
        });
      }
      
      // Update conversation preview to show "הגיב באימוגי" (Reacted with emoji) - ALWAYS for real-time
      // This runs for ALL users to show real-time updates via WebSocket
      // ALWAYS show "הגיב באימוגי" when ANY reaction arrives (even from me)
      // Count reactions EXACTLY like regular messages (only if from others and not active)
      if (!targetConversationId) {
        // If we can't find conversationId, try to find it from active conversation
        if (active && active._id) {
          targetConversationId = active._id;
        } else {
          // Can't update without conversationId - but don't return, try to find it from messages
          // This should rarely happen if backend sends conversationId in payload
        }
      }
      
      // If still no targetConversationId, we can't update - return early
      if (!targetConversationId) {
        console.warn('[ChatPopup] onReaction: No targetConversationId found, cannot update');
        return;
      }
      
      
      const isInActive = !!(active && active._id === targetConversationId);
      const isFromMe = String(userId) === String(me._id);
      
      
      // ALWAYS update "הגיב באימוגי" when reaction arrives (even if from me)
      setConversations(prev => {
        const idx = prev.findIndex(c => c._id === targetConversationId);
        
        // Calculate if we should count this reaction (same logic as regular messages)
        // Count if: NOT from me, NOT in active conversation
        const shouldCount = !isFromMe && !isInActive;
        const inc = shouldCount ? 1 : 0;
        
        
        const updatedLast = {
          _id: messageId,
          textPreview: `הגיב באימוגי ${emoji}`,
          sentAt: new Date().toISOString(),
          sentBy: userId,
          tombstone: false,
        };
        
        
        if (idx >= 0) {
          const c = prev[idx];
          const next = [...prev];
          const patched: any = patchAI({
            ...c,
            lastMessage: updatedLast,
            unreadCount: isInActive ? 0 : (c.unreadCount || 0) + inc
          });
          (patched as any).lastForMe = updatedLast;
          next[idx] = patched;
          
          
          // Move to top for visibility (same as regular messages)
          const [moved] = next.splice(idx, 1);
          return [moved, ...next];
        } else {
          // Conversation not found in list - create stub (same as regular messages)
          const stub: any = patchAI({
            _id: targetConversationId,
            type: "dm",
            participants: [{ userId: userId }],
            lastMessage: updatedLast,
            unreadCount: inc,
          } as Conversation);
          (stub as any).lastForMe = updatedLast;
          return [stub, ...prev];
        }
      });
    };

    const onReactionRemoved = ({ messageId, userId }: { messageId: string; userId?: string }) => {
      if (!userId) return;
      
      // Find the message to get conversationId
      let targetConversationId: string | null = null;
      
      // Check in active messages first
      if (active && messages.length > 0) {
        const msg = messages.find(m => m._id === messageId);
        if (msg) {
          targetConversationId = msg.conversationId;
        }
      }
      
      // If not found, check in conversations cache
      if (!targetConversationId) {
        for (const conv of conversations) {
          if (conv.lastMessage?._id === messageId) {
            targetConversationId = conv._id;
            break;
          }
        }
      }
      
      // Remove specific user's reaction from state
      setMessageReactions(prev => {
        const currentReactions: Array<{ emoji: string; userId: string }> = (prev[messageId] || []) as Array<{ emoji: string; userId: string }>;
        const updatedReactions = currentReactions.filter((r: { emoji: string; userId: string }) => String(r.userId) !== String(userId));
        
        if (updatedReactions.length === 0) {
          // No reactions left, remove the entry
          const next = { ...prev };
          delete next[messageId];
          return next;
        }
        
        return {
          ...prev,
          [messageId]: updatedReactions
        };
      });
      
      // Update reactions in cached messages if present
      if (targetConversationId && messagesCacheRef.current[targetConversationId]) {
        const cached = messagesCacheRef.current[targetConversationId];
        const msgIndex = cached.findIndex((m: any) => m._id === messageId);
        if (msgIndex >= 0) {
          const msg = cached[msgIndex];
          const currentReactions = msg.reactions || [];
          const updatedReactions = currentReactions.filter((r: any) => String(r.userId) !== String(userId));
          
          const updated = [...cached];
          updated[msgIndex] = { 
            ...updated[msgIndex], 
            reactions: updatedReactions.length > 0 ? updatedReactions : undefined
          };
          messagesCacheRef.current[targetConversationId] = updated;
        }
      }
      
      // Update reactions in current messages if this conversation is active
      if (active && active._id === targetConversationId) {
        setMessages(prev => {
          const msgIndex = prev.findIndex((m: any) => m._id === messageId);
          if (msgIndex >= 0) {
            const msg = prev[msgIndex];
            const currentReactions = msg.reactions || [];
            const updatedReactions = currentReactions.filter((r: any) => String(r.userId) !== String(userId));
            
            const updated = [...prev];
            updated[msgIndex] = { 
              ...updated[msgIndex], 
              reactions: updatedReactions.length > 0 ? updatedReactions : undefined
            };
            return updated;
          }
          return prev;
        });
      }
    };

    if (!company || !me._id) {
      // Cleanup existing subscriptions if conditions are not met
      // This is handled by the return function of the useEffect
      return;
    }

    // Centralized subscriptions using socketManager
    const unsubMsgNew = socketManager.subscribe("message:new", onMsg);
    const unsubConvCreated = socketManager.subscribe("conversation:created", onConv);
    const unsubConvUpdated = socketManager.subscribe("conversation:updated", onConvUpdated);
    const unsubMsgRead = socketManager.subscribe("messages:read", onRead);
    const unsubMsgDeleted = socketManager.subscribe("message:deleted", onDeleted);
    const unsubTyping = socketManager.subscribe("typing", onTyping);
    const unsubReaction = socketManager.subscribe("message:reaction", onReaction);
    const unsubReactionRemoved = socketManager.subscribe("message:reaction-removed", onReactionRemoved);

    return () => {
      unsubMsgNew();
      unsubConvCreated();
      unsubConvUpdated();
      unsubMsgRead();
      unsubMsgDeleted();
      unsubTyping();
      unsubReaction();
      unsubReactionRemoved();
    };
  }, [API_BASE, company, active?._id, active?.botUserId, me._id, team, userCache]);

  /* open conversation */
  async function openConversation(c: Conversation) {
    const patched = patchAI(c);
    
    // Check cache first - show cached messages immediately for instant display
    const cachedMessages = messagesCacheRef.current[patched._id];
    if (cachedMessages && cachedMessages.length > 0) {
      // Extract reactions from cached messages
      const reactions: Record<string, Array<{ emoji: string; userId: string }>> = {};
      cachedMessages.forEach((msg: any) => {
        if (msg.reactions && Array.isArray(msg.reactions) && msg.reactions.length > 0) {
          reactions[msg._id] = msg.reactions;
        }
      });
      if (Object.keys(reactions).length > 0) {
        setMessageReactions(prev => ({ ...prev, ...reactions }));
      }
      
      // Don't clear "הגיב באימוגי" preview when opening conversation
      // Keep it visible in the conversation list until user actually sees the messages
      // The preview will be updated naturally when new messages arrive or when markRead is called
      
      // Set messages BEFORE setting active to ensure they render immediately
      setMessages(cachedMessages);
    } else {
      // Clear messages only if no cache
      setMessages([]);
    }
    
    // Set active after messages to ensure they render together
    setActive(patched);
    
    setPane("list");
    setSelectMode(false);
    setSelectedIds(new Set());
    setTypedTextById({});
    Object.values(typeTimers.current).forEach(t => clearTimeout(t));
    typeTimers.current = {};
    
    // Join conversation room for WebSocket events (including reactions)
    emit("join:conversation", { conversationId: patched._id });
    
    // Scroll immediately for cached messages
    if (cachedMessages && cachedMessages.length > 0) {
      setTimeout(() => {
        scrollToBottom("auto");
      }, 0);
    }

    // Ensure all participants are in userCache (for presence display)
    if (patched.type === "group" && patched.participants) {
      setUserCache(prev => {
        const updated = { ...prev };
        for (const p of patched.participants) {
          if (!updated[p.userId]) {
            updated[p.userId] = {
              _id: p.userId,
              fullName: p.fullName || "",
              presence: { status: "offline" }
            };
          }
        }
        return updated;
      });
      
      // Request presence for all participants via socket
      const participantIds = patched.participants
        .map(p => p.userId)
        .filter(id => id !== me._id);
      if (participantIds.length > 0) {
        // Emit a request for presence (if the server supports it)
        // Otherwise, the presence:update events should come through automatically
        emit("presence:request", { userIds: participantIds });
      }
    }

    // Check if this is from a search result
    const firstMatched = c.matchedMessages && c.matchedMessages.length > 0 ? c.matchedMessages[0] : null;
    if (firstMatched) {
      setSearchMessageId(firstMatched._id);
    } else {
      setSearchMessageId(null);
    }

    // Load messages in background and update cache (only if not in cache or cache is stale)
    const hasCache = messagesCacheRef.current[patched._id] && messagesCacheRef.current[patched._id].length > 0;
    const hasSearchMessage = firstMatched !== null;
    
    let sorted: Message[] = [];
    
    // Only load from API if no cache or if we need to find a specific message
    if (!hasCache || hasSearchMessage) {
      try {
        const msgs = await getMessages(company, patched._id, 50) as Message[];
        sorted = msgs.slice().sort((a,b) => new Date(a.createdAt||0).getTime() - new Date(b.createdAt||0).getTime());
        
        // Extract reactions from messages and update messageReactions state
        const reactions: Record<string, Array<{ emoji: string; userId: string }>> = {};
        sorted.forEach((msg: any) => {
          if (msg.reactions && Array.isArray(msg.reactions) && msg.reactions.length > 0) {
            reactions[msg._id] = msg.reactions;
          }
        });
        if (Object.keys(reactions).length > 0) {
          setMessageReactions(prev => ({ ...prev, ...reactions }));
        }
        
        // Don't clear "הגיב באימוגי" preview when opening conversation
        // Keep it visible in the conversation list until user actually sees the messages
        // The preview will be updated naturally when new messages arrive or when markRead is called
        
        // Update cache
        messagesCacheRef.current[patched._id] = sorted;
        
        // Update messages (only if still on same conversation)
        if (active?._id === patched._id || !active) {
          setMessages(sorted);
          
          // If searching for a specific message, scroll to it instead of bottom
          if (hasSearchMessage && firstMatched) {
            // Wait for React to render the messages
            setTimeout(() => {
              const msgEl = document.getElementById(`msg-${firstMatched._id}`);
              if (msgEl) {
                msgEl.scrollIntoView({ behavior: "auto", block: "center" });
                // Remove highlight after 3 seconds
                setTimeout(() => setSearchMessageId(null), 3000);
              } else {
                // Retry after a bit more time if message not found yet
                setTimeout(() => {
                  const retryEl = document.getElementById(`msg-${firstMatched._id}`);
                  if (retryEl) {
                    retryEl.scrollIntoView({ behavior: "auto", block: "center" });
                    setTimeout(() => setSearchMessageId(null), 3000);
                  } else {
                    scrollToBottom("auto");
                  }
                }, 100);
              }
            }, 50);
          } else {
            // Scroll to bottom after messages load (use setTimeout to ensure DOM is updated)
            setTimeout(() => {
              scrollToBottom("auto");
            }, 0);
          }
        }
      } catch (err: any) {
        // Handle 403 Forbidden (user might have been removed from group or lost access)
        const errorMsg = err?.message || String(err || '');
        if (errorMsg.includes('403') || errorMsg.includes('Forbidden')) {
          setErr("אין לך גישה לשיחה זו");
          setActive(null);
          setMessages([]);
          // Remove from conversations list
          setConversations(prev => prev.filter(c => c._id !== patched._id));
          return;
        }
        // Handle other errors gracefully
        console.error("Error opening conversation:", err);
        setErr(err?.message || "שגיאה בטעינת השיחה");
        setActive(null);
        setMessages([]);
        return;
      }
    }

    // mark read locally + sync preview + advance lastReadAt + POST /read
    const messagesToUse = hasCache && !hasSearchMessage ? messagesCacheRef.current[patched._id] : (sorted.length > 0 ? sorted : messagesCacheRef.current[patched._id] || []);
    const newest = messagesToUse.length > 0 ? messagesToUse[messagesToUse.length - 1] : null;
    if (newest?.createdAt) lastReadAtRef.current[patched._id] = newest.createdAt;

    // Update lastMessage only if it's not "הגיב באימוגי" (keep reaction preview visible)
    setConversations(prev =>
      prev.map(x => {
        if (x._id === patched._id) {
          // If current lastMessage is "הגיב באימוגי", keep it (don't override with computeLastPreview)
          if (x.lastMessage?.textPreview?.startsWith('הגיב באימוגי')) {
            return { ...(x as any), unreadCount: 0 };
          }
          // Otherwise, update with computed preview
          const newLast = computeLastPreview(messagesToUse);
          return { ...(x as any), lastMessage: newLast, lastForMe: newLast, unreadCount: 0 };
        }
        return x;
      })
    );

    if (newest?._id) {
      markRead(patched._id, newest._id, newest.createdAt);
    }

    // Scroll to search message if found, otherwise to bottom (only if we have cache and not loading from API)
    if (hasCache && !hasSearchMessage) {
      // If we have cache and no search message, scroll to bottom immediately
      setTimeout(() => scrollToBottom("auto"), 0);
    } else if (hasCache && firstMatched) {
      // If we have cache and searching for a message, scroll to it
      setTimeout(() => {
        const msgEl = document.getElementById(`msg-${firstMatched._id}`);
        if (msgEl) {
          msgEl.scrollIntoView({ behavior: "auto", block: "center" });
          setTimeout(() => setSearchMessageId(null), 3000);
        } else {
          setTimeout(() => {
            const retryEl = document.getElementById(`msg-${firstMatched._id}`);
            if (retryEl) {
              retryEl.scrollIntoView({ behavior: "auto", block: "center" });
              setTimeout(() => setSearchMessageId(null), 3000);
            } else {
              scrollToBottom("auto");
            }
          }, 100);
        }
      }, 50);
    }
    setTypingIds(new Set());
    setBotTyping(false);
    setOpenSeenFor(null);
  }

  /* start DM */
async function openDM(otherUserId: string) {
  if (!otherUserId || !/^[0-9a-fA-F]{24}$/.test(otherUserId)) { setErr("Invalid teammate id"); return; }
  try {
    // If forwarding, close the current conversation first to avoid flicker
    const isForwarding = forwardingMessages && forwardingMessages.size > 0;
    if (isForwarding) {
      setActive(null);
      setPane("list");
      // Small delay to ensure UI updates before opening new conversation
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    const created = await createConversation(company, { type: "dm", participantIds: [otherUserId] });
    const hydrated: Conversation = {
      ...created,
      participants: (created?.participants || []).map((p: any) => ({
        ...p,
        fullName: userCache[p.userId]?.fullName || p.fullName
      })),
    };
    setConversations(prev => (prev.find(c => c._id === hydrated._id) ? prev : [patchAI(hydrated), ...prev]));
    await openConversation(hydrated);
    
    // If forwarding messages, send them to the new conversation
    if (isForwarding && forwardingMessageTextsRef.current) {
      try {
        const messageTexts = forwardingMessageTextsRef.current;
        await sendChatMessage(company, created._id, `${T.forwardedPrefix}${messageTexts}`);
        setForwardingMessages(null);
        forwardingMessageTextsRef.current = "";
        toast.success("הודעות הועברו בהצלחה!");
      } catch (e) {
        toast.error("שגיאה בהעברת הודעות");
        console.error("Forward error:", e);
        setForwardingMessages(null);
        forwardingMessageTextsRef.current = "";
      }
    }
  } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
}

  /* create/open AI conversation — gated by company slug */
  async function openAI() {
/*     if (company !== "test") {
      alert("Lumira AI is an add-on service. Contact us to enable it for your company.");
      return;
    } */
    try {
      const ai = await createAIConversation(company) as Conversation;
      const pretty = patchAI({ ...ai, isAI: true, title: BOT_NAME });
      setConversations(prev => [pretty, ...prev.filter(c => c._id !== ai._id)]);
      await openConversation(pretty);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  /* Typewriter for AI messages */
  function startTypewriter(msg: Message) {
    const full = msg.text || "";
    if (!full) return;
    setTypedTextById(prev => ({ ...prev, [msg._id]: "" }));
    const stepChars = Math.max(TYPE_MIN_STEP, Math.ceil(full.length / TYPE_SEGMENTS));
    let i = 0;
    const tick = () => {
      i += stepChars;
      const slice = full.slice(0, i);
      setTypedTextById(prev => ({ ...prev, [msg._id]: slice }));
      if (i < full.length) {
        typeTimers.current[msg._id] = window.setTimeout(tick, TYPE_INTERVAL_MS);
      } else {
        setBotTyping(false);
      }
    };
    tick();
  }

  /* send message (optimistic) */
  async function send() {
    if (!active || !draft.trim()) return;
    const replyText = replyingTo ? `↪ ${replyingTo.text?.slice(0, 50)}${replyingTo.text && replyingTo.text.length > 50 ? '...' : ''}\n\n` : '';
    const toSend = replyText + draft.trim();
    const replyToId = replyingTo?._id;
    setDraft("");
    setVoiceInterim('');
    setReplyingTo(null);

    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    tempIds.current.add(tempId);
    const optimistic: Message = {
      _id: tempId,
      company,
      conversationId: active._id,
      createdBy: me._id,
      type: "text",
      text: toSend,
      createdAt: new Date().toISOString(),
      readBy: [{ userId: me._id, at: new Date().toISOString() }]
    };
    setMessages(prev => {
      const updated = [...prev, optimistic];
      // Update cache
      if (active?._id) {
        messagesCacheRef.current[active._id] = updated;
      }
      return updated;
    });

    setConversations(prev => {
      const idx = prev.findIndex(c => c._id === active._id);
      if (idx === -1) return prev;
      const c = prev[idx];
      const updatedLast = {
        _id: tempId,
        textPreview: toSend.slice(0,120),
        sentAt: optimistic.createdAt,
        sentBy: me._id
      };
      const next = [...prev];
      const patched: any = { ...c, lastMessage: updatedLast };
      (patched as any).lastForMe = updatedLast;
      next[idx] = patched;
      const [moved] = next.splice(idx, 1);
      return [moved, ...next];
    });

    // Always scroll to bottom when user sends a message (use auto for better performance)
    setTimeout(() => scrollToBottom("auto"), 0);

    try {
      await sendChatMessage(company, active._id, toSend);

      if (active.isAI) {
        setBotTyping(true);
        sendAIChatMessage(company, active._id, toSend).catch(() => {
          setBotTyping(false);
          const fail: Message = {
            _id: `fail_${Date.now()}`,
            company, conversationId: active._id,
            createdBy: active.botUserId || "bot",
            type: "text",
            text: "Sorry — I couldn't reply right now. Please try again.",
            createdAt: new Date().toISOString()
          };
          setMessages(prev => {
            const updated = [...prev, fail];
            // Update cache
            if (active?._id) {
              messagesCacheRef.current[active._id] = updated;
            }
            return updated;
          });
          // Always scroll for bot error messages (they're from the bot, not the user)
          setTimeout(() => scrollToBottom("auto"), 0);
        });
      }

      if (typingSent.current) {
        emit("typing", { conversationId: active._id, isTyping: false });
        typingSent.current = false;
      }
    } catch (e: any) {
      const errorMsg = e?.response?.data?.error || e?.message || String(e);
      const isStorageFull = e?.response?.data?.code === 'STORAGE_FULL' || errorMsg.includes('storage') || errorMsg.includes('full');
      
      if (isStorageFull) {
        toast.error('האחסון מלא. אנא פנו לתמיכה או שדרגו את התוכנית כדי לשלוח הודעות.');
      } else {
        setErr(errorMsg);
      }
      setMessages(prev => {
        const updated = prev.filter(m => m._id !== tempId);
        // Update cache
        if (active?._id) {
          messagesCacheRef.current[active._id] = updated;
        }
        return updated;
      });
      setConversations(prev => {
        const idx = prev.findIndex(c => c._id === active._id);
        if (idx === -1) return prev;
        // Restore last message if send failed
        return prev;
      });
    }
  }

  function onDraftChange(v: string) {
    // Update draft immediately - no delay, no blocking
    setDraft(v);

    if (!active) return;

    // Handle typing indicator asynchronously to not block text input
    setTimeout(() => {
      // Send typing indicator if not already sent
      if (!typingSent.current) {
        emit("typing", { conversationId: active._id, isTyping: true });
        typingSent.current = true;
      }

      // Clear existing timer
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
        typingTimer.current = null;
      }

      // Set new timer to stop typing indicator after 1.2 seconds of no typing
      typingTimer.current = setTimeout(() => {
        if (active && typingSent.current) {
          emit("typing", { conversationId: active._id, isTyping: false });
          typingSent.current = false;
        }
        if (typingTimer.current) {
          clearTimeout(typingTimer.current);
          typingTimer.current = null;
        }
      }, 1200);
    }, 0);
  }

  /* helpers */
  const nameOf = (id?: string) =>
    id ? (userCache[id]?.fullName ||
    active?.participants.find((p) => p.userId === id)?.fullName ||
    (id === me._id ? me.fullName : (active?.isAI && id === active.botUserId ? BOT_NAME : "Unknown"))) : "Unknown";

  const dmOtherId = useMemo(
    () => (active?.type === "dm" ? active.participants.find((p) => p.userId !== me._id)?.userId : undefined),
    [active, me._id]
  );

  const seenByOthers = (m: Message) => {
    if (!m.readBy) return false;
    if (active?.type === "dm" && dmOtherId) return m.readBy.some((r) => r.userId === dmOtherId);
    if (active?.type === "group") {
      const others = active.participants.map((p) => p.userId).filter((id) => id !== me._id);
      return others.every((id) => m.readBy!.some((r) => r.userId === id));
    }
    return false;
  };

  const grouped = useMemo(() => {
    const out: { day: string; blocks: { mine: boolean; authorId: string; items: Message[] }[] }[] = [];
    const chronological = messages.slice().sort((a,b) => new Date(a.createdAt||0).getTime() - new Date(b.createdAt||0).getTime());
    const byDay: Record<string, Message[]> = {};
    for (const m of chronological) {
      const key = dayLabel(m.createdAt);
      (byDay[key] ||= []).push(m);
    }
    for (const [day, arr] of Object.entries(byDay)) {
      const blocks: { mine: boolean; authorId: string; items: Message[] }[] = [];
      for (const m of arr) {
        const mine = String(m.createdBy) === me._id;
        const authorId = String(m.createdBy);
        const last = blocks[blocks.length - 1];
        if (last && last.mine === mine && last.authorId === authorId) last.items.push(m);
        else blocks.push({ mine, authorId, items: [m] });
      }
      out.push({ day, blocks });
    }
    return out;
  }, [messages, me._id]);

  const headerTitle =
    active?.type === "group"
      ? active.title || "קבוצה"
      : active
      ? active.isAI ? BOT_NAME : (active.participants.find((p) => p.userId !== me._id)?.fullName || "הודעה ישירה")
      : T.messages;

  // show all conversations (including AI) — search covers any conversation
  const visibleConversations = useMemo(
    () => filteredConversations,
    [filteredConversations]
  );
  const totalUnreadVisible = useMemo(
    () => visibleConversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
    [visibleConversations]
  );

  useEffect(() => {
  if (!conversations.length || !Object.keys(userCache).length) return;
  setConversations(prev =>
    prev.map(c => ({
      ...c,
      participants: (c.participants || []).map(p =>
        p.fullName ? p : { ...p, fullName: userCache[p.userId]?.fullName || p.fullName }
      ),
    }))
  );
}, [userCache, conversations.length]);
  // Report 0 to the outer badge while popup is open
  useEffect(() => {
    if (isOpen) onUnreadChange?.(0);
  }, [isOpen, onUnreadChange]);

  // When popup is closed, resume sending the real total
  useEffect(() => {
    if (!isOpen) onUnreadChange?.(totalUnreadVisible);
  }, [isOpen, totalUnreadVisible, onUnreadChange]);

  // Auto-scroll to bottom when messages change (only if should auto-scroll)
  // Use refs to avoid unnecessary re-renders
  const shouldAutoScrollRef = useRef(shouldAutoScroll);
  const isUserScrolledUpRef = useRef(isUserScrolledUp);
  
  useEffect(() => {
    shouldAutoScrollRef.current = shouldAutoScroll;
    isUserScrolledUpRef.current = isUserScrolledUp;
  }, [shouldAutoScroll, isUserScrolledUp]);

  useEffect(() => {
    if (!active || messages.length === 0) return;
    
    // Check if user is actually scrolled up by checking the container directly
    const container = messagesContainerRef.current;
    const isActuallyScrolledUp = container ? !isNearBottom(container) : isUserScrolledUpRef.current;
    
    // Only auto-scroll if:
    // 1. User is NOT scrolled up (at bottom)
    // 2. AND shouldAutoScroll is true (user sent message or initial load)
    // This prevents auto-scrolling when user is scrolled up and new message arrives
    if (!isActuallyScrolledUp && shouldAutoScrollRef.current) {
      // Use setTimeout with 0 delay instead of requestAnimationFrame for better performance
      const timeoutId = setTimeout(() => {
        scrollToBottom("auto");
        setHasNewMessages(false);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [messages.length, active?._id]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    if (!showEmojiPicker) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.emoji-picker-container') && !target.closest('button[title="הוסף אימוג\'י"]')) {
        setShowEmojiPicker(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  // Close quick reactions when clicking outside (not auto-close)
  useEffect(() => {
    if (!quickReactionMessageId) return;
    
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking on the reaction popup itself
      if (target.closest('.quick-reactions-popup') || target.closest('.full-emoji-picker-popup')) {
        return;
      }
      setQuickReactionMessageId(null);
      setQuickReactionPosition(null);
      setShowFullEmojiPicker(false);
    };
    
    // Close on click/touch outside (with small delay to avoid immediate close)
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [quickReactionMessageId]);

  /* ========== Render ========== */
  // Unified scroll lock hook
  const { lockScroll, unlockScroll } = useBodyScrollLock();

  // Prevent body scroll on mobile when chat is open and auto-expand on mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 640; // sm breakpoint
      setIsMobile(mobile);
      if (isOpen && mobile) {
        lockScroll();
        setIsExpanded(true); // Always expanded on mobile
      } else {
        unlockScroll();
      }
    };
    
    const mobile = window.innerWidth < 640;
    setIsMobile(mobile);
    if (isOpen && mobile) {
      lockScroll();
      setIsExpanded(true); // Always expanded on mobile
      window.addEventListener('resize', handleResize);
      return () => {
        unlockScroll();
        window.removeEventListener('resize', handleResize);
      };
    } else if (isOpen) {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  const sideClass = "left-2 sm:left-2 md:left-2";
  // Full responsive container: full screen on mobile, popup on desktop
  const containerClasses = isExpanded || isMobile
  ? "fixed inset-0 w-full h-full bg-white rounded-none shadow-2xl border-0 overflow-hidden z-[1000]"
  : `fixed inset-0 sm:inset-auto sm:bottom-20 ${sideClass} sm:w-[400px] md:w-[420px] lg:w-[440px] sm:max-h-[78vh] sm:rounded-2xl rounded-none bg-white shadow-2xl border border-gray-200 overflow-hidden z-[1000]`;
  // Hide footer badge when inside a conversation
  const footerBadge = active ? 0 : totalUnreadVisible;

  /* helpers for seen lists */
  const othersWhoSaw = (m: Message) =>
    (m.readBy || []).map(r => r.userId).filter(uid => uid !== me._id);

  useEffect(() => {
    setVoiceInterim('');
  }, [active?._id]);

  return (
    <div dir="rtl" lang="he" className={isOpen ? containerClasses : "hidden"}>
      <style>{`
        @keyframes blink { 0% {opacity:.2} 20%{opacity:1} 100%{opacity:.2} }
        .typing-dots span { display:inline-block; width:6px; height:6px; margin:0 2px; border-radius:9999px; background:#9aa3af; animation: blink 1.2s infinite; }
        .typing-dots span:nth-child(2){ animation-delay:.2s }
        .typing-dots span:nth-child(3){ animation-delay:.4s }
        .glow { box-shadow: 0 10px 30px rgba(59,130,246,.25), inset 0 0 0 1px rgba(59,130,246,.06); }
        .modal-bg { backdrop-filter: blur(2px); }
        [dir="rtl"] .ltr {
          direction: ltr;
          unicode-bidi: embed;
        }
        [dir="rtl"] .flip-x { transform: scaleX(-1); }
        .chat-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .chat-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .chat-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.3);
          border-radius: 3px;
        }
        .chat-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.5);
        }
        .chat-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
        }
        /* Safe area support for mobile devices (notch, bottom bar) */
        .safe-area-inset-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
        .safe-area-inset-top {
          padding-top: env(safe-area-inset-top, 0);
        }
        @supports (padding: max(0px)) {
          .safe-area-inset-bottom {
            padding-bottom: max(env(safe-area-inset-bottom, 0px), 8px);
          }
          .safe-area-inset-top {
            padding-top: max(env(safe-area-inset-top, 0px), 0px);
          }
        }
      `}</style>
      {/* Header - Responsive for mobile and desktop */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white/80 backdrop-blur border-b sticky top-0 z-30 safe-area-inset-top">
        {/* Left: back + avatar + title */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-row-reverse flex-1">
          {active && (
            <button
              type="button"
              title="Back"
              onClick={() => { setActive(null); setPane("list"); setSelectMode(false); setSelectedIds(new Set()); }}
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full grid place-items-center text-slate-700 hover:bg-slate-100 active:scale-[.98] outline-none focus:outline-none transition-colors flex-shrink-0"
            >
              <ArrowLeftIcon className="flip-x" size={18} />
            </button>
          )}

          <div className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-50
                          grid place-items-center text-blue-700 font-semibold shadow-inner select-none flex-shrink-0">
            {active?.isAI ? "🤖" : avatar(headerTitle)}
            {(() => {
              // In DM, show the other person's status; in group/AI, show my status
              if (active?.type === "dm" && dmOtherId) {
                const otherUser = userCache[dmOtherId];
                const otherStatus = otherUser?.presence?.status || "offline";
                return (
                  <span className={`absolute -right-0.5 -bottom-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ring-2 ring-white ${statusDot(otherStatus)}`} />
                );
              }
              // For groups and AI, show my status
              return (
                <span className={`absolute -right-0.5 -bottom-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ring-2 ring-white ${statusDot(myStatus)}`} />
              );
            })()}
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-sm sm:text-base font-bold tracking-wide text-slate-900 truncate max-w-[40vw] sm:max-w-[50vw] select-none leading-tight">
              {headerTitle}
            </div>
            {(active && (botTyping || typingIds.size > 0)) && (
              <div className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-blue-600 font-medium truncate select-none leading-relaxed">
                {active.type === "dm" ? T.typingDm : "מישהו מקליד…"}
              </div>
            )}
          </div>
        </div>

        {/* Right: single compact toolbar - Responsive */}
        <div className="flex items-center gap-1 sm:gap-2 rounded-full bg-slate-50 px-1.5 sm:px-2 py-1 sm:py-1.5 border border-slate-200 shadow-sm select-none flex-shrink-0">
          {/* Members (groups only) */}
          {active && active.type === "group" && (
            <button
              type="button"
              title="חברים"
              onClick={() => setShowMembers(true)}
              className="h-8 sm:h-9 px-2 sm:px-3 rounded-full inline-flex items-center gap-1 sm:gap-2 text-xs font-medium text-slate-700
                        hover:bg-white active:scale-[.98] outline-none focus:outline-none transition-colors"
            >
              <UsersIcon size={14} className="sm:w-4 sm:h-4" />
              <span className="hidden md:inline">חברים</span>
              <span className="inline-flex items-center justify-center min-w-[18px] sm:min-w-[20px] h-[18px] sm:h-[20px] px-1 sm:px-1.5
                              rounded-full bg-blue-600 text-white text-[10px] sm:text-[11px] font-semibold leading-[18px] sm:leading-[20px] select-none pointer-events-none">
                {active.participants.length}
              </span>
            </button>
          )}

          {/* Select toggle: icon-only until active */}
          {active && (
            <button
              type="button"
              title={selectMode ? "סיים בחירה" : "בחר הודעות"}
              onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}
              className={[
                "h-8 w-8 sm:h-9 sm:w-9 rounded-full inline-flex items-center justify-center outline-none focus:outline-none active:scale-[.98] transition-colors",
                selectMode ? "bg-blue-600 text-white hover:bg-blue-700" : "text-slate-700 hover:bg-white"
              ].join(" ")}
            >
              {selectMode ? (
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <CheckSquareIcon size={16} className="sm:w-[18px] sm:h-[18px]" />
              )}
            </button>
          )}

          {/* Status (compact dot + chevron; no border) */}
          <div className="px-0.5 sm:px-1">
            <StatusChip
              value={myStatus}
              onChange={(v) => setMyStatus(v)}
              /* make StatusChip button look minimal */
            />
          </div>

          {/* Expand / Restore - Hide on mobile since it's always expanded */}
          {!isMobile && (
            <button
              type="button"
              title={isExpanded ? T.restore : T.expand}
              onClick={() => setIsExpanded(v => !v)}
              className="h-8 w-8 sm:h-9 sm:w-9 rounded-full grid place-items-center text-slate-700 hover:bg-white active:scale-[.98] outline-none focus:outline-none transition-colors"
            >
              <span className="text-sm sm:text-base leading-none">{isExpanded ? "▢" : "⤢"}</span>
            </button>
          )}

          {/* Close */}
          <button
            type="button"
            title={T.close}
            onClick={onClose}
            className="h-8 w-8 sm:h-9 sm:w-9 rounded-full grid place-items-center text-rose-600 hover:bg-rose-50 active:scale-[.98] outline-none focus:outline-none transition-colors"
          >
            <XIcon size={16} className="sm:w-[18px] sm:h-[18px]" />
          </button>
        </div>
      </div>

      {/* search (outside a conversation) - Responsive */}
      {!active && (
        <div className="sticky top-[48px] sm:top-[56px] z-20 bg-white/95 backdrop-blur border-b">
          <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2">
            <div className="text-xs sm:text-[13px] font-semibold text-gray-700 hidden sm:block">{T.browse}</div>
            <div className="flex-1" />
            <div className="relative w-full sm:w-[48%] md:w-[42%]">
              <input
                className="w-full bg-gray-50 border border-gray-300 rounded-xl px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm
                          focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500
                          placeholder:text-gray-400 text-right placeholder:text-right"
                placeholder={T.searchPlaceholder}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              {searchLoading && (
                <div className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] sm:text-xs text-gray-500">
                  מחפש...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Body - Responsive height */}
      {!active ? (
        <div className="flex flex-col" style={{ height: isExpanded || isMobile ? "calc(100vh - 48px - 52px)" : "68vh" }}>
          <div className={`flex-1 overflow-y-auto chat-scrollbar px-2 sm:px-3 py-1.5 sm:py-2 space-y-1 sm:space-y-1.5 ${isExpanded || isMobile ? 'pb-[52px]' : 'pb-[42px]'}`}>
            {err && <div className="text-sm text-red-600">{err}</div>}
            {loading && (
              <div className="flex flex-col items-center justify-center py-16">
                {/* Messenger-style animated bubbles */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div 
                    className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                    style={{ 
                      animationDelay: '0ms',
                      animationDuration: '1.4s',
                      animationIterationCount: 'infinite'
                    }}
                  ></div>
                  <div 
                    className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                    style={{ 
                      animationDelay: '160ms',
                      animationDuration: '1.4s',
                      animationIterationCount: 'infinite'
                    }}
                  ></div>
                  <div 
                    className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                    style={{ 
                      animationDelay: '320ms',
                      animationDuration: '1.4s',
                      animationIterationCount: 'infinite'
                    }}
                  ></div>
                </div>
                <div className="text-sm text-gray-500 font-medium">טוען שיחות...</div>
                <style>{`
                  @keyframes messenger-bounce {
                    0%, 60%, 100% {
                      transform: translateY(0);
                      opacity: 0.7;
                    }
                    30% {
                      transform: translateY(-10px);
                      opacity: 1;
                    }
                  }
                  .animate-bounce {
                    animation: messenger-bounce 1.4s ease-in-out infinite;
                  }
                `}</style>
              </div>
            )}
            {searchLoading && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="relative w-8 h-8 mb-3">
                  <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                </div>
                <div className="text-sm text-gray-600">מחפש...</div>
              </div>
            )}
            {pane === "list" && visibleConversations.length > 0 && (
              <div className="space-y-1.5">
                {visibleConversations.map((c) => {
                  const title =
                    c.type === "group"
                      ? c.title || "קבוצה"
                      : c.participants.find((p) => p.userId !== me._id)?.fullName || "הודעה ישירה";

                  const last: any = (c as any).lastForMe || c.lastMessage;
                  const typers = typingByConv[c._id] ? Array.from(typingByConv[c._id]) : [];
                  let typingPreview: string | null = null;
                  if (typers.length) {
                    if (c.type === "dm") {
                      typingPreview = T.typingDm;
                    } else {
                      const names = typers.slice(0, 2).map(uid => nameInConv(c, uid));
                      typingPreview = names.length === 1 ? T.typingGroupOne(names[0]) : T.typingGroupTwo(names[0], names[1]);
                    }
                  }

                  const preview = typingPreview
                    ? typingPreview
                    : (!last ? T.noMessages : last.tombstone ? T.msgDeleted : (last.textPreview || T.noMessages));
                  const when = last?.sentAt ? fmtHM(last.sentAt) : "";

                  // Highlight search query in preview
                  const isSearching = filter.trim().length >= 2;
                  const highlightSearch = (text: string, query: string) => {
                    if (!isSearching || !text) return text;
                    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
                    return parts.map((part, i) => 
                      part.toLowerCase() === query.toLowerCase() 
                        ? <mark key={i} className="bg-yellow-300 rounded px-0.5">{part}</mark>
                        : part
                    );
                  };

                  return (
                      <button
                        key={c._id}
                        className={[
                          "w-full text-right px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-xl hover:bg-blue-50/60 border flex items-center gap-2 sm:gap-3 transition shadow-sm",
                          isSearching ? "border-yellow-300 bg-yellow-50/30" : "border-gray-200"
                        ].join(" ")}
                        onClick={() => void openConversation(c)}
                      >
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold flex-shrink-0 text-xs sm:text-sm">
                        {avatar(title)}
                      </div>
                      <div className="min-w-0 flex-1 text-right">
                        <div className="font-medium flex items-center gap-1.5 sm:gap-2 justify-end text-sm sm:text-base">
                          <span className="truncate">{title}</span>
                          {c.type === "group" && (
                            <span className="inline-flex items-center gap-0.5 sm:gap-1 text-[9px] sm:text-[10px] text-blue-600 bg-blue-50 px-1 sm:px-1.5 py-0.5 rounded-full font-medium shrink-0" title="קבוצה">
                              <UsersIcon size={9} className="sm:w-[10px] sm:h-[10px]" />
                              <span className="hidden sm:inline">קבוצה</span>
                            </span>
                          )}
                        </div>
                        <div
                          className={[
                            "text-[11px] sm:text-xs truncate",
                            typingPreview ? "text-green-600 font-medium italic" : "text-gray-500"
                          ].join(" ")}
                        >
                          {isSearching && typeof preview === "string" ? highlightSearch(preview, filter.trim()) : preview}
                        </div>
                      </div>
                      <div className="ltr flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                        {when && <div className="text-[10px] sm:text-[11px] text-gray-400 whitespace-nowrap">{when}</div>}
                        {!!c.unreadCount && (
                          <span className="min-w-[16px] sm:min-w-[18px] h-[16px] sm:h-[18px] px-0.5 sm:px-1 rounded-full bg-green-600 text-white text-[9px] sm:text-[10px] flex items-center justify-center font-semibold">
                            {c.unreadCount}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {pane === "new-dm" && (
              <div className="flex flex-col h-full">
                {forwardingMessages && forwardingMessages.size > 0 && (
                  <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 text-sm text-blue-700">
                    <div className="font-medium mb-1">מעביר {forwardingMessages.size} הודעה{forwardingMessages.size > 1 ? 'ות' : ''}</div>
                    <button
                      onClick={() => {
                        setForwardingMessages(null);
                        setPane("list");
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      {T.cancel}
                    </button>
                  </div>
                )}
                <Roster team={team} meId={me._id} filter={filter} onMessage={(id) => void openDM(id)} />
              </div>
            )}
            {pane === "new-group" && (
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <input
                    className="flex-1 bg-gray-50 border border-gray-300 rounded-xl px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm
                               focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
                    placeholder={T.groupNamePlaceholder}
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                  />
                  <button
                    className="px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-blue-600 text-white text-xs sm:text-sm disabled:opacity-50 font-medium"
                    disabled={!groupName.trim() || groupSel.size === 0}
                    onClick={async () => {
                      try {
                        const created = await createConversation(company, {
                          type: "group",
                          title: groupName.trim(),
                          participantIds: Array.from(groupSel),
                        });
                        setConversations(prev => [created, ...prev.filter(c => c._id !== created._id)]);
                        setGroupName("");
                        setGroupSel(new Set());
                        await openConversation(created);
                      } catch (e) {
                        setErr(e instanceof Error ? e.message : String(e));
                      }
                    }}
                  >
                    {T.create}
                  </button>
                </div>

                <Roster
                  team={team}
                  meId={me._id}
                  filter={filter}
                  selectable
                  selected={groupSel}
                  onToggle={(id, on) => {
                    const next = new Set(groupSel);
                    if (on) next.add(id); else next.delete(id);
                    setGroupSel(next);
                  }}
                />
              </div>
            )}
          </div>

          {/* FOOTER NAV — tabs - Responsive */}
          <div className="sticky bottom-0 z-20 bg-white/95 backdrop-blur border-t safe-area-inset-bottom">
            <div className="flex items-center justify-around px-1 sm:px-2 py-1.5 sm:py-1">
              <IconTab size="sm" active={pane === "list"} onClick={() => setPane("list")} icon={<ChatIcon size={14} className="sm:w-4 sm:h-4" />} label={T.messages} badge={active ? 0 : footerBadge} />
              <IconTab size="sm" active={pane === "new-dm"} onClick={() => setPane("new-dm")} icon={<UserPlusIcon size={14} className="sm:w-4 sm:h-4" />} label={T.newDM} />
              <IconTab 
                size="sm" 
                active={pane === "new-group"} 
                onClick={() => setPane("new-group")} 
                icon={
                  <div className="relative">
                    <UsersIcon size={14} className="sm:w-4 sm:h-4" />
                    <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full border border-white" title="קבוצה" />
                  </div>
                } 
                label={T.newGroup} 
              />
              {!featuresLoading && !hiddenFromSidebar.includes('lumira-ai') && (
                <IconTab
                  size="sm"
                  active={false}
                  onClick={() =>
                    company === "test"
                      ? void openAI()
                      : alert(T.aiAddonMsg)
                  }
                  icon={<RobotIcon size={14} className="sm:w-4 sm:h-4" />}
                  label={T.lumiraAI}
                  title={company === "test" ? T.lumiraOpen : T.lumiraPremium}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Conversation view - Responsive height */
        <div className="flex flex-col relative" style={{ height: isExpanded || isMobile ? "calc(100vh - 48px)" : "68vh" }}>
          {/* Bulk actions bar */}
          {selectMode && (
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
              <div className="text-sm text-gray-600">{T.selected(selectedIds.size)}</div>
              <div className="flex gap-2">
                <button className="px-2 py-1 text-sm rounded border" onClick={() => setSelectedIds(new Set(messages.map(m => m._id)))}>
                  {T.selectAll}
                </button>
                <button
                  className="px-2 py-1 text-sm rounded border"
                  onClick={() => {
                    const texts = messages.filter(m => selectedIds.has(m._id)).map(m => m.text || "").join("\n");
                    navigator.clipboard.writeText(texts).catch(() => {});
                  }}
                >
                  {T.copy}
                </button>
                <button
                  className="px-2 py-1 text-sm rounded border"
                  onClick={() => {
                    // Save selected messages for forwarding (store message texts, not just IDs)
                    const selectedMessages = messages.filter(m => selectedIds.has(m._id));
                    const messageTexts = selectedMessages.map(m => m.text || "").join("\n");
                    forwardingMessageTextsRef.current = messageTexts;
                    setForwardingMessages(new Set(selectedIds));
                    // Close current conversation first to avoid flicker
                    setActive(null);
                    setSelectMode(false);
                    setSelectedIds(new Set());
                    // Small delay to ensure UI updates before opening new-dm pane
                    setTimeout(() => {
                      setPane("new-dm");
                    }, 0);
                  }}
                >
                  {T.forward}
                </button>
                <button
                  className="px-2 py-1 text-sm rounded bg-red-600 text-white"
                  onClick={async () => {
                    const ids = Array.from(selectedIds);

                    // Optimistic remove + refresh preview if needed
                    setMessages(prev => {
                      const next = prev.filter(m => !selectedIds.has(m._id));
                      setConversations(list => list.map(c => {
                        if (!active || c._id !== active._id) return c;
                        const oldPreviewId = c.lastMessage?._id;
                        if (oldPreviewId && ids.includes(oldPreviewId)) {
                          const newLast = computeLastPreview(next);
                          const patched: any = { ...c, lastMessage: newLast };
                          (patched as any).lastForMe = newLast;
                          return patched;
                        }
                        return c;
                      }));
                      return next;
                    });

                    setSelectedIds(new Set());
                    setSelectMode(false);

                    try {
                      await bulkDeleteMessages(company, ids, false);
                    } catch {
                      await Promise.allSettled(
                        ids.map((id) => deleteMessage(company, id))
                      );
                    }
                  }}
                >
                  {T.delete}
                </button>
              </div>
            </div>
          )}

          <div 
            ref={messagesContainerRef} 
            className="flex-1 overflow-y-auto chat-scrollbar p-2 sm:p-3 bg-white space-y-3 sm:space-y-4"
            style={{ willChange: 'scroll-position' }}
          >
            {grouped.map(({ day, blocks }) => (
              <div key={day}>
                <div className="sticky top-0 z-10 flex justify-center my-1.5 sm:my-2">
                  <span className="text-[10px] sm:text-[11px] text-gray-500 bg-gray-100 rounded-full px-2.5 sm:px-3 py-0.5 sm:py-1">{day}</span>
                </div>
                {blocks.map((b, idx) => (
                  <div key={idx} className={`flex ${b.mine ? "justify-end" : "justify-start"} mb-1.5 sm:mb-2`}>
                    {!b.mine && (
                      <div className="w-6 h-6 sm:w-7 sm:h-7 ml-1.5 sm:ml-2 rounded-full bg-gray-200 flex items-center justify-center text-[10px] sm:text-xs font-semibold flex-shrink-0">
                        {active?.isAI && b.authorId === active.botUserId ? "🤖" : avatar(nameOf(b.authorId))}
                      </div>
                    )}
                    <div className="space-y-1 sm:space-y-1.5 max-w-[85%] sm:max-w-[78%]">
                      {!b.mine && <div className="text-[10px] sm:text-xs text-gray-600 mr-1 font-medium">{nameOf(b.authorId)}</div>}
                      {b.items.map((m) => {
                        const mine = String(m.createdBy) === me._id;
                        const checked = selectedIds.has(m._id);
                        const display = m.tombstone ? null : (typedTextById[m._id] ?? m.text ?? "");
                        const others = othersWhoSaw(m);
                        const canShowSeen = others.length > 0;
                        return (
                          <div key={m._id} id={`msg-${m._id}`} className="relative group">
                            {selectMode && (
                              <input
                                type="checkbox"
                                className={`absolute ${mine ? "-left-6" : "-right-6"} top-1 w-4 h-4`}
                                checked={checked}
                                onChange={(e) => {
                                  const next = new Set(selectedIds);
                                  if (e.target.checked) next.add(m._id); else next.delete(m._id);
                                  setSelectedIds(next);
                                }}
                              />
                            )}

                            {/* Reply button - appears on hover */}
                            {!selectMode && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReplyingTo(m);
                                  // Scroll to composer
                                  setTimeout(() => {
                                    const composer = document.querySelector('textarea');
                                    composer?.focus();
                                  }, 100);
                                }}
                                className={`absolute ${mine ? "-left-10" : "-right-10"} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-200 p-2 rounded-full bg-white shadow-md hover:shadow-lg border border-gray-200 hover:border-blue-400 ${mine ? "hover:bg-blue-50 hover:border-blue-500" : "hover:bg-gray-50"}`}
                                title="השב"
                              >
                                <svg className="w-4 h-4 text-gray-600 hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                </svg>
                              </button>
                            )}

                            <div
                              className={`px-3 py-2 sm:px-4 sm:py-2.5 rounded-2xl sm:rounded-2xl shadow-sm whitespace-pre-wrap break-words transition-all duration-200 text-sm sm:text-base ${
                                mine ? "bg-blue-600 text-white rounded-br-sm hover:bg-blue-700" : "bg-gray-100 text-gray-900 rounded-bl-sm hover:bg-gray-200"
                              } ${selectMode && checked ? "ring-2 ring-blue-400 ring-offset-1" : ""} ${searchMessageId === m._id ? "ring-4 ring-yellow-400 animate-pulse shadow-lg" : ""}`}
                              onMouseDown={(e) => {
                                if (selectMode) return;
                                const startX = e.clientX;
                                const startY = e.clientY;
                                
                                longPressTimerRef.current = window.setTimeout(() => {
                                  setQuickReactionMessageId(m._id);
                                  setQuickReactionPosition({ x: e.clientX, y: e.clientY });
                                }, 500);
                                
                                const handleMouseUp = () => {
                                  if (longPressTimerRef.current) {
                                    clearTimeout(longPressTimerRef.current);
                                    longPressTimerRef.current = null;
                                  }
                                  document.removeEventListener('mouseup', handleMouseUp);
                                  document.removeEventListener('mousemove', handleMouseMove);
                                };
                                
                                const handleMouseMove = (moveEvent: MouseEvent) => {
                                  const moved = Math.abs(moveEvent.clientX - startX) > 10 || Math.abs(moveEvent.clientY - startY) > 10;
                                  if (moved && longPressTimerRef.current) {
                                    clearTimeout(longPressTimerRef.current);
                                    longPressTimerRef.current = null;
                                  }
                                };
                                
                                document.addEventListener('mouseup', handleMouseUp);
                                document.addEventListener('mousemove', handleMouseMove);
                              }}
                              onTouchStart={(e) => {
                                if (selectMode) return;
                                // Only allow long-press for messages from others (not mine)
                                if (mine) return;
                                const touch = e.touches[0];
                                const startX = touch.clientX;
                                const startY = touch.clientY;
                                
                                longPressTimerRef.current = window.setTimeout(() => {
                                  setQuickReactionMessageId(m._id);
                                  setQuickReactionPosition({ x: touch.clientX, y: touch.clientY });
                                }, 500);
                                
                                const handleTouchEnd = () => {
                                  if (longPressTimerRef.current) {
                                    clearTimeout(longPressTimerRef.current);
                                    longPressTimerRef.current = null;
                                  }
                                  document.removeEventListener('touchend', handleTouchEnd);
                                  document.removeEventListener('touchmove', handleTouchMove);
                                };
                                
                                const handleTouchMove = (moveEvent: TouchEvent) => {
                                  if (moveEvent.touches.length > 0) {
                                    const moved = Math.abs(moveEvent.touches[0].clientX - startX) > 10 || Math.abs(moveEvent.touches[0].clientY - startY) > 10;
                                    if (moved && longPressTimerRef.current) {
                                      clearTimeout(longPressTimerRef.current);
                                      longPressTimerRef.current = null;
                                    }
                                  }
                                };
                                
                                document.addEventListener('touchend', handleTouchEnd);
                                document.addEventListener('touchmove', handleTouchMove);
                              }}
                            >
                              {m.tombstone ? (
                                <em className={mine ? "text-blue-100" : "text-gray-600"}>{T.msgDeleted}</em>
                              ) : (
                                display
                              )}
                              <div className={`ltr text-[10px] mt-1 ${mine ? "text-blue-100" : "text-gray-500"} flex items-center gap-2 justify-end`}>
                                <span>{fmtHM(m.createdAt)}</span>
                                {mine && (
                                  <>
                                    <span title={seenByOthers(m) ? T.read : T.delivered}>
                                      {seenByOthers(m) ? "✓✓" : "✓"}
                                    </span>
                                    {canShowSeen && (
                                      <button
                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${mine ? "bg-blue-500/30 text-white" : "bg-gray-200 text-gray-700"}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenSeenFor(prev => prev === m._id ? null : m._id);
                                        }}
                                        title={T.viewWhoSaw}
>
                                        <EyeIcon size={12} />
                                        <span className="text-[10px]">{others.length}</span>
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>

                              {/* "Seen by" popover */}
                              {openSeenFor === m._id && (
                                <div
                                  className={`absolute ${mine ? "right-0" : "left-0"} mt-1 z-20`}
                                  style={{ transform: "translateY(4px)" }}
                                >
                                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-[180px]">
                                    <div className="text-[11px] font-semibold text-gray-700 mb-1">{T.seenBy}</div>
                                    <ul className="max-h-48 overflow-auto chat-scrollbar">
                                      {others.map(uid => {
                                        const r = (m.readBy || []).find(rr => rr.userId === uid);
                                        return (
                                          <li key={uid} className="text-[12px] text-gray-700 flex items-center justify-between gap-3 py-1">
                                            <span className="flex items-center gap-2">
                                              <span className={`inline-block w-2.5 h-2.5 rounded-full ${statusDot(userCache[uid]?.presence?.status)}`} />
                                              <span className="truncate max-w-[110px]">{nameOf(uid)}</span>
                                            </span>
                                            <span className="text-gray-400">{fmtHM(r?.at)}</span>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                    <div className="mt-1 flex justify-end">
                                      <button
                                        className="text-[11px] px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200"
                                        onClick={() => setOpenSeenFor(null)}
                                      >
                                        {T.close}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Reaction badges on message (Instagram-style) - multiple reactions from different users */}
                              {(() => {
                                const reactions: Array<{ emoji: string; userId: string }> = (m.reactions || messageReactions[m._id] || []) as Array<{ emoji: string; userId: string }>;
                                if (!Array.isArray(reactions) || reactions.length === 0) return null;
                                
                                const myReaction = reactions.find((r: { emoji: string; userId: string }) => String(r.userId) === String(me._id));
                                const otherReactions = reactions.filter((r: { emoji: string; userId: string }) => String(r.userId) !== String(me._id));
                                
                                return (
                                  <div className={`absolute ${mine ? 'left-0' : 'right-0'} -bottom-2 flex items-center gap-1 flex-wrap max-w-[120px]`}>
                                    {/* My reaction (interactive) */}
                                    {myReaction && (
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          const now = Date.now();
                                          const lastClick = reactionClickTimesRef.current[m._id] || 0;
                                          const timeDiff = now - lastClick;
                                          
                                          if (timeDiff < 300 && timeDiff > 0) {
                                            // Double-click detected - remove my reaction
                                            if (company) {
                                              // Optimistic update - remove my reaction
                                              setMessageReactions(prev => {
                                                const currentReactions: Array<{ emoji: string; userId: string }> = (prev[m._id] || []) as Array<{ emoji: string; userId: string }>;
                                                const updatedReactions = currentReactions.filter((r: { emoji: string; userId: string }) => String(r.userId) !== String(me._id));
                                                
                                                if (updatedReactions.length === 0) {
                                                  const next = { ...prev };
                                                  delete next[m._id];
                                                  return next;
                                                }
                                                
                                                return {
                                                  ...prev,
                                                  [m._id]: updatedReactions
                                                };
                                              });
                                              
                                              // Update in messages state
                                              setMessages(prev => {
                                                const msgIndex = prev.findIndex(msg => msg._id === m._id);
                                                if (msgIndex >= 0) {
                                                  const msg = prev[msgIndex];
                                                  const currentReactions = msg.reactions || [];
                                                  const updatedReactions = currentReactions.filter((r: any) => String(r.userId) !== String(me._id));
                                                  
                                                  const updated = [...prev];
                                                  updated[msgIndex] = { 
                                                    ...updated[msgIndex], 
                                                    reactions: updatedReactions.length > 0 ? updatedReactions : undefined
                                                  };
                                                  return updated;
                                                }
                                                return prev;
                                              });
                                              
                                              // Update cache
                                              if (active?._id && messagesCacheRef.current[active._id]) {
                                                const cached = messagesCacheRef.current[active._id];
                                                const msgIndex = cached.findIndex((msg: any) => msg._id === m._id);
                                                if (msgIndex >= 0) {
                                                  const msg = cached[msgIndex];
                                                  const currentReactions = msg.reactions || [];
                                                  const updatedReactions = currentReactions.filter((r: any) => String(r.userId) !== String(me._id));
                                                  
                                                  const updated = [...cached];
                                                  updated[msgIndex] = { 
                                                    ...updated[msgIndex], 
                                                    reactions: updatedReactions.length > 0 ? updatedReactions : undefined
                                                  };
                                                  messagesCacheRef.current[active._id] = updated;
                                                }
                                              }
                                              
                                              // Send to backend - backend will emit WebSocket event automatically
                                              try {
                                                await removeMessageReaction(company, m._id);
                                                // No need to emit here - backend emits "message:reaction-removed" event after saving
                                              } catch (err) {
                                                console.error("Failed to remove reaction:", err);
                                                // Revert optimistic update on error
                                                const originalReactions = reactions;
                                                setMessageReactions(prev => ({
                                                  ...prev,
                                                  [m._id]: originalReactions
                                                }));
                                              }
                                            }
                                            reactionClickTimesRef.current[m._id] = 0;
                                          } else {
                                            // Single click - open emoji picker to change my reaction
                                            const rect = (e.target as HTMLElement).getBoundingClientRect();
                                            setQuickReactionMessageId(m._id);
                                            setQuickReactionPosition({ x: rect.left + rect.width / 2, y: rect.top });
                                            setShowFullEmojiPicker(true);
                                            reactionClickTimesRef.current[m._id] = now;
                                          }
                                        }}
                                        className="flex items-center justify-center w-6 h-6 bg-white rounded-full border-2 border-blue-600 shadow-md hover:scale-110 transition-transform cursor-pointer"
                                        title="Click to change, double-click to remove"
                                      >
                                        <span className="text-sm">{myReaction.emoji}</span>
                                      </button>
                                    )}
                                    
                                    {/* Other users' reactions (read-only) */}
                                    {otherReactions.map((reaction: { emoji: string; userId: string }, idx: number) => (
                                      <div
                                        key={`${reaction.userId}-${idx}`}
                                        className="flex items-center justify-center w-6 h-6 bg-white rounded-full border-2 border-gray-300 shadow-md"
                                        title={`Reacted with ${reaction.emoji}`}
                                      >
                                        <span className="text-sm">{reaction.emoji}</span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* Quick Reactions Popup (Instagram-style) */}
            <AnimatePresence>
              {quickReactionMessageId && quickReactionPosition && (
                <>
                  {!showFullEmojiPicker ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.5, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.5, y: 20 }}
                      transition={{ duration: 0.2 }}
                      className="fixed z-50 pointer-events-none"
                      style={{
                        left: `${quickReactionPosition.x}px`,
                        top: `${quickReactionPosition.y - 60}px`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      <div className="flex items-center gap-1.5 bg-white rounded-full px-1.5 py-1 shadow-2xl border border-gray-200">
                        {['❤️', '😂', '😮', '😢'].map((emoji) => (
                          <button
                            key={emoji}
                            onClick={async (e) => {
                              e.stopPropagation();
                              // Add reaction to message (not send as new message)
                              if (quickReactionMessageId && company) {
                                // Optimistic update - add/update my reaction in messages state
                                setMessages(prev => {
                                  const msgIndex = prev.findIndex(msg => msg._id === quickReactionMessageId);
                                  if (msgIndex >= 0) {
                                    const msg = prev[msgIndex];
                                    const currentReactions = msg.reactions || [];
                                    const existingIndex = currentReactions.findIndex((r: any) => String(r.userId) === String(me._id));
                                    
                                    let updatedReactions: any[];
                                    if (existingIndex >= 0) {
                                      updatedReactions = [...currentReactions];
                                      updatedReactions[existingIndex] = { emoji, userId: me._id };
                                    } else {
                                      updatedReactions = [...currentReactions, { emoji, userId: me._id }];
                                    }
                                    
                                    const updated = [...prev];
                                    updated[msgIndex] = { ...updated[msgIndex], reactions: updatedReactions };
                                    return updated;
                                  }
                                  return prev;
                                });
                                
                                // Update cache
                                if (active?._id && messagesCacheRef.current[active._id]) {
                                  const cached = messagesCacheRef.current[active._id];
                                  const msgIndex = cached.findIndex((msg: any) => msg._id === quickReactionMessageId);
                                  if (msgIndex >= 0) {
                                    const msg = cached[msgIndex];
                                    const currentReactions = msg.reactions || [];
                                    const existingIndex = currentReactions.findIndex((r: any) => String(r.userId) === String(me._id));
                                    
                                    let updatedReactions: any[];
                                    if (existingIndex >= 0) {
                                      updatedReactions = [...currentReactions];
                                      updatedReactions[existingIndex] = { emoji, userId: me._id };
                                    } else {
                                      updatedReactions = [...currentReactions, { emoji, userId: me._id }];
                                    }
                                    
                                    const updated = [...cached];
                                    updated[msgIndex] = { ...updated[msgIndex], reactions: updatedReactions };
                                    messagesCacheRef.current[active._id] = updated;
                                  }
                                }
                                
                                // Update state - IMMEDIATE for fast UI
                                const isNewReaction = !messageReactions[quickReactionMessageId]?.some((r: any) => String(r.userId) === String(me._id));
                                
                                setMessageReactions(prev => {
                                  const currentReactions: Array<{ emoji: string; userId: string }> = (prev[quickReactionMessageId] || []) as Array<{ emoji: string; userId: string }>;
                                  const existingIndex = currentReactions.findIndex((r: { emoji: string; userId: string }) => String(r.userId) === String(me._id));
                                  
                                  let updatedReactions: Array<{ emoji: string; userId: string }>;
                                  if (existingIndex >= 0) {
                                    updatedReactions = [...currentReactions];
                                    updatedReactions[existingIndex] = { emoji, userId: me._id };
                                  } else {
                                    updatedReactions = [...currentReactions, { emoji, userId: me._id }];
                                  }
                                  
                                  return {
                                    ...prev,
                                    [quickReactionMessageId]: updatedReactions
                                  };
                                });
                                
                                // IMMEDIATE update of conversation list and unread count for real-time
                                if (active?._id) {
                                  const targetConvId = active._id;
                                  const isInActive = true; // We're in the conversation
                                  
                                  // Update conversation preview immediately
                                  setConversations(prev => {
                                    const idx = prev.findIndex(c => c._id === targetConvId);
                                    if (idx >= 0) {
                                      const c = prev[idx];
                                      const updatedLast = {
                                        _id: quickReactionMessageId,
                                        textPreview: `הגיב באימוגי ${emoji}`,
                                        sentAt: new Date().toISOString(),
                                        sentBy: me._id,
                                        tombstone: false,
                                      };
                                      
                                      const next = [...prev];
                                      const patched: any = patchAI({
                                        ...c,
                                        lastMessage: updatedLast,
                                        unreadCount: 0 // Active conversation, no unread
                                      });
                                      (patched as any).lastForMe = updatedLast;
                                      next[idx] = patched;
                                      
                                      // Move to top
                                      const [moved] = next.splice(idx, 1);
                                      return [moved, ...next];
                                    }
                                    return prev;
                                  });
                                }
                                
                                // Send to backend - backend will emit WebSocket event automatically
                                try {
                                  await addMessageReaction(company, quickReactionMessageId, emoji);
                                  // No need to emit here - backend emits "message:reaction" event after saving
                                } catch (err) {
                                  console.error("Failed to add reaction:", err);
                                  // Revert optimistic update on error - restore previous reactions
                                  setMessages(prev => {
                                    const msgIndex = prev.findIndex(msg => msg._id === quickReactionMessageId);
                                    if (msgIndex >= 0) {
                                      const msg = prev[msgIndex];
                                      const currentReactions = msg.reactions || [];
                                      // Remove my reaction that was just added
                                      const revertedReactions = currentReactions.filter((r: any) => String(r.userId) !== String(me._id));
                                      
                                      const updated = [...prev];
                                      updated[msgIndex] = { 
                                        ...updated[msgIndex], 
                                        reactions: revertedReactions.length > 0 ? revertedReactions : undefined
                                      };
                                      return updated;
                                    }
                                    return prev;
                                  });
                                  setMessageReactions(prev => {
                                    const currentReactions: Array<{ emoji: string; userId: string }> = (prev[quickReactionMessageId] || []) as Array<{ emoji: string; userId: string }>;
                                    const revertedReactions = currentReactions.filter((r: { emoji: string; userId: string }) => String(r.userId) !== String(me._id));
                                    
                                    if (revertedReactions.length === 0) {
                                      const next = { ...prev };
                                      delete next[quickReactionMessageId];
                                      return next;
                                    }
                                    
                                    return {
                                      ...prev,
                                      [quickReactionMessageId]: revertedReactions
                                    };
                                  });
                                }
                              }
                              setQuickReactionMessageId(null);
                              setQuickReactionPosition(null);
                              setShowFullEmojiPicker(false);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            className="text-2xl hover:scale-140 transition-transform cursor-pointer pointer-events-auto p-1"
                            title={emoji}
                          >
                            {emoji}
                          </button>
                        ))}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            // Open emoji picker immediately without delay
                            setShowFullEmojiPicker(true);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            // Open emoji picker immediately on mouse down
                            setShowFullEmojiPicker(true);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                          className="text-lg font-bold text-gray-600 hover:text-gray-800 transition-all cursor-pointer pointer-events-auto w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100"
                          title="More emojis"
                        >
                          +
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8, y: 20 }}
                      transition={{ duration: 0.2 }}
                      className="fixed z-50 pointer-events-none full-emoji-picker-popup"
                      style={{
                        left: `${quickReactionPosition.x}px`,
                        top: `${quickReactionPosition.y - 100}px`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-3 w-64 h-64 overflow-y-auto pointer-events-auto">
                        <div className="grid grid-cols-8 gap-1">
                          {['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '😵', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐', '😕', '😟', '🙁', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻', '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾', '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁️', '👅', '👄', '💋', '🩸', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '🎁', '🎈', '🎉', '🎊', '🎋', '🎌', '🎍', '🎎', '🎏', '🎐', '🎀', '🧧', '🔮', '🎆', '🎇', '✨', '🎂', '🎃', '🎄', '🎅', '🤶', '🧑‍🎄', '🧨', '🎗️', '🎟️', '🎫', '🎖️', '🏆', '🏅', '🥇', '🥈', '🥉', '⚽', '⚾', '🥎', '🏀', '🏐', '🏈', '🏉', '🎾', '🥏', '🎳', '🏏', '🏑', '🏒', '🥍', '🏓', '🏸', '🥊', '🥋', '🥅', '⛳', '🏌️', '🏄', '🏇', '🧘', '🏊', '⛷️', '🏂', '🏋️', '🚴', '🚵', '🤸', '🤼', '🤽', '🤾', '🤹', '🧗', '🏃', '🚶', '💃', '🕺', '🕴️', '👯', '🧖', '🛀', '🛌', '👭', '👫', '👬', '💏', '💑', '👪', '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛', '🚜', '🛻', '🚃', '🚋', '🚞', '🚝', '🚄', '🚅', '🚆', '🚇', '🚈', '🚉', '🚊', '🚝', '🚞', '🚟', '🚠', '🚡', '🛰️', '🚀', '🛸', '✈️', '🛫', '🛬', '🪂', '💺', '🚁', '🚲', '🛴', '🛵', '🏍️', '🛺', '🚨', '🚔', '🚍', '🚘', '🚖', '🌍', '🌎', '🌏', '🌐', '🗺️', '🧭', '🏔️', '⛰️', '🌋', '🗻', '🏕️', '🏖️', '🏜️', '🏝️', '🏞️', '🏟️', '🏛️', '🏗️', '🧱', '🏘️', '🏚️', '🏠', '🏡', '🏢', '🏣', '🏤', '🏥', '🏦', '🏨', '🏩', '🏪', '🏫', '🏬', '🏭', '🏯', '🏰', '🗼', '🗽', '⛪', '🕌', '🛕', '🕍', '⛩️', '🕋', '⛲', '⛺', '🌁', '🌃', '🏙️', '🌄', '🌅', '🌆', '🌇', '🌉', '♨️', '🎠', '🎡', '🎢', '💈', '🎪', '🌌'].map((emoji) => (
                            <button
                              key={emoji}
                              onClick={async (e) => {
                                e.stopPropagation();
                                // Add reaction to message (not send as new message)
                                if (quickReactionMessageId && company) {
                                  // Optimistic update - add/update my reaction in messages state
                                  setMessages(prev => {
                                    const msgIndex = prev.findIndex(msg => msg._id === quickReactionMessageId);
                                    if (msgIndex >= 0) {
                                      const msg = prev[msgIndex];
                                      const currentReactions = msg.reactions || [];
                                      const existingIndex = currentReactions.findIndex((r: any) => String(r.userId) === String(me._id));
                                      
                                      let updatedReactions: any[];
                                      if (existingIndex >= 0) {
                                        updatedReactions = [...currentReactions];
                                        updatedReactions[existingIndex] = { emoji, userId: me._id };
                                      } else {
                                        updatedReactions = [...currentReactions, { emoji, userId: me._id }];
                                      }
                                      
                                      const updated = [...prev];
                                      updated[msgIndex] = { ...updated[msgIndex], reactions: updatedReactions };
                                      return updated;
                                    }
                                    return prev;
                                  });
                                  
                                  // Update cache
                                  if (active?._id && messagesCacheRef.current[active._id]) {
                                    const cached = messagesCacheRef.current[active._id];
                                    const msgIndex = cached.findIndex((msg: any) => msg._id === quickReactionMessageId);
                                    if (msgIndex >= 0) {
                                      const msg = cached[msgIndex];
                                      const currentReactions = msg.reactions || [];
                                      const existingIndex = currentReactions.findIndex((r: any) => String(r.userId) === String(me._id));
                                      
                                      let updatedReactions: any[];
                                      if (existingIndex >= 0) {
                                        updatedReactions = [...currentReactions];
                                        updatedReactions[existingIndex] = { emoji, userId: me._id };
                                      } else {
                                        updatedReactions = [...currentReactions, { emoji, userId: me._id }];
                                      }
                                      
                                      const updated = [...cached];
                                      updated[msgIndex] = { ...updated[msgIndex], reactions: updatedReactions };
                                      messagesCacheRef.current[active._id] = updated;
                                    }
                                  }
                                  
                                  // Update state - IMMEDIATE for fast UI
                                  const isNewReaction = !messageReactions[quickReactionMessageId]?.some((r: any) => String(r.userId) === String(me._id));
                                  
                                  setMessageReactions(prev => {
                                    const currentReactions: Array<{ emoji: string; userId: string }> = (prev[quickReactionMessageId] || []) as Array<{ emoji: string; userId: string }>;
                                    const existingIndex = currentReactions.findIndex((r: { emoji: string; userId: string }) => String(r.userId) === String(me._id));
                                    
                                    let updatedReactions: Array<{ emoji: string; userId: string }>;
                                    if (existingIndex >= 0) {
                                      updatedReactions = [...currentReactions];
                                      updatedReactions[existingIndex] = { emoji, userId: me._id };
                                    } else {
                                      updatedReactions = [...currentReactions, { emoji, userId: me._id }];
                                    }
                                    
                                    return {
                                      ...prev,
                                      [quickReactionMessageId]: updatedReactions
                                    };
                                  });
                                  
                                  // IMMEDIATE update of conversation list for real-time (ALWAYS, even if active)
                                  // This ensures "הגיב באימוגי" shows immediately when I react
                                  const targetConvId = active?._id;
                                  if (targetConvId) {
                                    // Update conversation preview immediately
                                    setConversations(prev => {
                                      const idx = prev.findIndex(c => c._id === targetConvId);
                                      if (idx >= 0) {
                                        const c = prev[idx];
                                        const updatedLast = {
                                          _id: quickReactionMessageId,
                                          textPreview: `הגיב באימוגי ${emoji}`,
                                          sentAt: new Date().toISOString(),
                                          sentBy: me._id,
                                          tombstone: false,
                                        };
                                        
                                        const next = [...prev];
                                        const patched: any = patchAI({
                                          ...c,
                                          lastMessage: updatedLast,
                                          unreadCount: 0 // Active conversation, no unread
                                        });
                                        (patched as any).lastForMe = updatedLast;
                                        next[idx] = patched;
                                        
                                        // Move to top for visibility
                                        const [moved] = next.splice(idx, 1);
                                        return [moved, ...next];
                                      }
                                      return prev;
                                    });
                                  }
                                  
                                  // Send to backend - backend will emit WebSocket event automatically
                                  try {
                                    await addMessageReaction(company, quickReactionMessageId, emoji);
                                    // No need to emit here - backend emits "message:reaction" event after saving
                                  } catch (err) {
                                    console.error("Failed to add reaction:", err);
                                    // Revert optimistic update on error - restore previous reactions
                                    setMessages(prev => {
                                      const msgIndex = prev.findIndex(msg => msg._id === quickReactionMessageId);
                                      if (msgIndex >= 0) {
                                        const msg = prev[msgIndex];
                                        const currentReactions = msg.reactions || [];
                                        // Remove my reaction that was just added
                                        const revertedReactions = currentReactions.filter((r: any) => String(r.userId) !== String(me._id));
                                        
                                        const updated = [...prev];
                                        updated[msgIndex] = { 
                                          ...updated[msgIndex], 
                                          reactions: revertedReactions.length > 0 ? revertedReactions : undefined
                                        };
                                        return updated;
                                      }
                                      return prev;
                                    });
                                    setMessageReactions(prev => {
                                      const currentReactions: Array<{ emoji: string; userId: string }> = (prev[quickReactionMessageId] || []) as Array<{ emoji: string; userId: string }>;
                                      const revertedReactions = currentReactions.filter((r: { emoji: string; userId: string }) => String(r.userId) !== String(me._id));
                                      
                                      if (revertedReactions.length === 0) {
                                        const next = { ...prev };
                                        delete next[quickReactionMessageId];
                                        return next;
                                      }
                                      
                                      return {
                                        ...prev,
                                        [quickReactionMessageId]: revertedReactions
                                      };
                                    });
                                  }
                                }
                                setQuickReactionMessageId(null);
                                setQuickReactionPosition(null);
                                setShowFullEmojiPicker(false);
                              }}
                              className="text-xl hover:bg-gray-100 rounded p-1 transition-colors cursor-pointer pointer-events-auto"
                              title={emoji}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </>
              )}
            </AnimatePresence>

            <div ref={bottomRef} />

            {/* Typing indicators bubble - positioned higher like Facebook */}
            <AnimatePresence>
              {(botTyping || typingIds.size > 0) && (
                <motion.div
                  key="typing"
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="flex justify-start mb-8 mt-3"
                >
                  <div className="px-4 py-2.5 rounded-2xl bg-gray-100 text-gray-700 inline-flex items-center gap-2.5 shadow-md border border-gray-200/50">
                    {[0, 1, 2].map((i) => (
                      <motion.span
                        key={i}
                        className="inline-block w-2.5 h-2.5 rounded-full bg-gray-500"
                        animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }}
                        transition={{ repeat: Infinity, duration: 1, ease: "easeInOut", delay: i * 0.15 }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* New message button - appears only when new message arrives while user is scrolled up */}
          {hasNewMessages && isUserScrolledUp && active && !hasClickedNewMessageButton && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20"
            >
              <button
                onClick={() => {
                  setHasNewMessages(false);
                  setHasClickedNewMessageButton(true); // Mark as clicked so it won't appear again until user scrolls up again
                  setShouldAutoScroll(true);
                  setIsUserScrolledUp(false);
                  scrollToBottom("auto");
                }}
                className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-full shadow-xl hover:bg-blue-700 hover:shadow-2xl transition-all duration-200 flex items-center gap-2 backdrop-blur-sm"
              >
                <motion.svg
                  animate={{ y: [0, -2, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </motion.svg>
                <span>הודעה חדשה</span>
              </button>
            </motion.div>
          )}

          {/* Composer - Responsive */}
          <div className="border-t p-2 sm:p-3 bg-white space-y-1.5 sm:space-y-2 safe-area-inset-bottom">
            {/* Reply preview */}
            {replyingTo && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="flex items-start gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-50 to-blue-100/50 border-l-4 border-blue-500 rounded-lg shadow-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-blue-700 mb-1.5 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    {String(replyingTo.createdBy) === me._id ? 'משיב לעצמך' : `משיב ל${nameOf(replyingTo.createdBy)}`}
                  </div>
                  <div className="text-sm text-gray-800 truncate leading-relaxed">{replyingTo.text?.slice(0, 100)}{replyingTo.text && replyingTo.text.length > 100 ? '...' : ''}</div>
                </div>
                <button
                  onClick={() => setReplyingTo(null)}
                  className="text-gray-500 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-full transition-all duration-200 flex-shrink-0"
                  title="ביטול"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </motion.div>
            )}
            <div className="flex items-center justify-end gap-0.5 sm:gap-1 text-[10px] sm:text-[11px] text-gray-500">
              {SPEECH_LANG_OPTIONS.map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full border transition-colors text-[10px] sm:text-[11px] ${
                    voiceLang === opt.code
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'border-gray-300 text-gray-600 hover:border-blue-500'
                  }`}
                  onClick={() => { setVoiceLang(opt.code); setVoiceInterim(''); }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex items-end gap-1.5 sm:gap-2 relative emoji-picker-container">
              {/* Emoji Picker - Responsive */}
              {showEmojiPicker && (
                <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-2xl p-2 sm:p-3 w-[280px] sm:w-72 h-56 sm:h-64 overflow-y-auto z-50 emoji-picker-container">
                  <div className="grid grid-cols-8 gap-1">
                    {['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '😵', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐', '😕', '😟', '🙁', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻', '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾', '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁️', '👅', '👄', '💋', '🩸', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '🎁', '🎈', '🎉', '🎊', '🎋', '🎌', '🎍', '🎎', '🎏', '🎐', '🎀', '🧧', '🔮', '🎆', '🎇', '✨', '🎂', '🎃', '🎄', '🎅', '🤶', '🧑‍🎄', '🧨', '🎗️', '🎟️', '🎫', '🎖️', '🏆', '🏅', '🥇', '🥈', '🥉', '⚽', '⚾', '🥎', '🏀', '🏐', '🏈', '🏉', '🎾', '🥏', '🎳', '🏏', '🏑', '🏒', '🥍', '🏓', '🏸', '🥊', '🥋', '🥅', '⛳', '🏌️', '🏄', '🏇', '🧘', '🏊', '⛷️', '🏂', '🏋️', '🚴', '🚵', '🤸', '🤼', '🤽', '🤾', '🤹', '🧗', '🏃', '🚶', '💃', '🕺', '🕴️', '👯', '🧖', '🛀', '🛌', '👭', '👫', '👬', '💏', '💑', '👪', '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛', '🚜', '🛻', '🚃', '🚋', '🚞', '🚝', '🚄', '🚅', '🚆', '🚇', '🚈', '🚉', '🚊', '🚝', '🚞', '🚟', '🚠', '🚡', '🛰️', '🚀', '🛸', '✈️', '🛫', '🛬', '🪂', '💺', '🚁', '🚲', '🛴', '🛵', '🏍️', '🛺', '🚨', '🚔', '🚍', '🚘', '🚖', '🌍', '🌎', '🌏', '🌐', '🗺️', '🧭', '🏔️', '⛰️', '🌋', '🗻', '🏕️', '🏖️', '🏜️', '🏝️', '🏞️', '🏟️', '🏛️', '🏗️', '🧱', '🏘️', '🏚️', '🏠', '🏡', '🏢', '🏣', '🏤', '🏥', '🏦', '🏨', '🏩', '🏪', '🏫', '🏬', '🏭', '🏯', '🏰', '🗼', '🗽', '⛪', '🕌', '🛕', '🕍', '⛩️', '🕋', '⛲', '⛺', '🌁', '🌃', '🏙️', '🌄', '🌅', '🌆', '🌇', '🌉', '♨️', '🎠', '🎡', '🎢', '💈', '🎪', '🌌'].map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => {
                          onDraftChange(draft + emoji);
                          setShowEmojiPicker(false);
                        }}
                        className="text-2xl hover:bg-gray-100 rounded p-1 transition-colors"
                        title={emoji}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className={`h-9 w-9 sm:h-11 sm:w-11 rounded-full border flex items-center justify-center transition-colors mb-1 flex-shrink-0 ${
                  showEmojiPicker 
                    ? 'bg-blue-50 border-blue-300' 
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
                title="הוסף אימוג'י"
              >
                <span className="text-lg sm:text-xl">😊</span>
              </button>
              <textarea
                className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 max-h-32 sm:max-h-40 min-h-[40px] sm:min-h-[44px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-xs sm:text-sm"
                dir={chatLangConfig.dir}
                style={{ textAlign: chatLangConfig.dir === 'rtl' ? 'right' : 'left' }}
                placeholder={active?.isAI ? `שאל את ${BOT_NAME}…` : "כתוב הודעה…"}
                value={draft}
                onChange={(e) => onDraftChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
                }}
              />
              <MicButton
                className="mb-1"
                lang={voiceLang}
                onFinal={(text) => {
                  const cleaned = text.trim();
                  if (!cleaned) return;
                  const appended = draft.trim().length > 0 ? `${draft} ${cleaned}` : cleaned;
                  onDraftChange(appended);
                }}
                onInterim={setVoiceInterim}
                disabled={!active}
              />
              <button
                className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-blue-600 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 active:bg-blue-800 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm"
                onClick={() => void send()}
                disabled={!draft.trim()}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                <span>שלח</span>
              </button>
            </div>
            {voiceInterim && (
              <div className="text-xs text-gray-500/70 px-1">{voiceInterim}</div>
            )}
          </div>
        </div>
      )}

      {/* --- MEMBERS PANEL (Groups) --- */}
      <AnimatePresence>
        {showMembers && active && active.type === "group" && (
          <motion.div
            className="fixed inset-0 z-[1100] modal-bg bg-black/10"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => { setShowMembers(false); setAddMode(false); }}
          >
            <motion.div
              className="absolute inset-0 sm:inset-auto sm:left-[420px] sm:bottom-28 sm:w-[360px] sm:max-h-[70vh] sm:rounded-2xl rounded-none bg-white shadow-2xl border border-gray-200 overflow-hidden"
              initial={{ y: 20, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-2 border-b flex items-center justify-between">
                <div className="font-semibold text-gray-900 text-sm">
                  {addMode ? T.addMembers :  T.membersTitle(active.title)}
                </div>
                <div className="flex items-center gap-2">
                  {!addMode && active?.createdBy === me._id && (
                    <button
                      className="text-xs px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-40"
                      title={T.addMembers}
                      onClick={() => setAddMode(true)}
                      disabled={addCandidates.length === 0}
                    >
                      + {T.add}
                    </button>
                  )}
                  <button
                    className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                    onClick={() => { setShowMembers(false); setAddMode(false); }}
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* List mode: show existing members */}
              {!addMode && (
                <ul className="divide-y max-h-[60vh] overflow-y-auto chat-scrollbar">
                  {active.participants.map((p) => {
                    const u = userCache[p.userId];
                    // Also check team list for presence data
                    const teamUser = team.find(t => t._id === p.userId);
                    const name = u?.fullName || p.fullName || (p.userId === me._id ? me.fullName : p.userId);
                    const isRemoving = removing === p.userId;
                    const isMe = p.userId === me._id;
                    // Use myStatus for "Me", otherwise check userCache, then team, then default to offline
                    const presenceStatus = isMe 
                      ? myStatus 
                      : (u?.presence?.status || teamUser?.presence?.status || "offline");
                    const isOnline = presenceStatus === "online";
                    const isAdmin = active?.createdBy === p.userId;
                    return (
                      <li key={p.userId} className="flex items-center justify-between px-3 py-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center font-semibold">
                            {name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium flex items-center gap-2">
                              <span className="truncate">{name}</span>
                              {isAdmin && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 shrink-0" title="יוצר הקבוצה">
                                  {T.admin}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{u?.role || T.member}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block rounded-full ${isAdmin && isOnline ? "w-3 h-3 ring-2 ring-green-300" : "w-2.5 h-2.5"} ${statusDot(presenceStatus)}`}
                            title={presenceStatus || "offline"}
                          />
                          {!isMe && active?.createdBy === me._id && (
                            <button
                              className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                              onClick={() => {
                                if (window.confirm(`האם אתה בטוח שברצונך להסיר את ${name} מהקבוצה?`)) {
                                  removeMemberFromGroup(p.userId);
                                }
                              }}
                              disabled={isRemoving || adding}
                              title={T.removeMember}
                            >
                              {isRemoving ? (
                                <span className="text-xs">{T.removing}</span>
                              ) : (
                                <XIcon size={14} />
                              )}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Add mode: pick candidates not in the group yet */}
              {addMode && (
                <div className="p-2">
                  {addCandidates.length === 0 ? (
                    <div className="text-sm text-gray-500 px-1 py-6 text-center">
                      {T.everyoneAlready}
                    </div>
                  ) : (
                    <>
                      <Roster
                        team={addCandidates}
                        meId={me._id}
                        filter={""}
                        selectable
                        selected={addSel}
                        onToggle={(id, on) => {
                          const next = new Set(addSel);
                          if (on) next.add(id); else next.delete(id);
                          setAddSel(next);
                        }}
                      />
                      <div className="flex justify-between items-center gap-2 mt-2">
                        <button
                          className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                          onClick={() => { setAddMode(false); setAddSel(new Set()); }}
                        >
                         {T.cancel}
                        </button>
                        <button
                          className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-40"
                          disabled={addSel.size === 0 || adding}
                          onClick={() => addMembersToActiveGroup(Array.from(addSel))}
                        >
                          {adding ? T.adding : T.addN(addSel.size)}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

/* ========== Roster ========== */
function Roster(props: {
  team: User[]; meId: string; filter: string;
  selectable?: boolean; selected?: Set<string>;
  onToggle?: (id: string, on: boolean) => void;
  onMessage?: (id: string) => void;
}) {
  const { team, meId, filter, selectable, selected, onToggle, onMessage } = props;
  const q = filter.trim().toLowerCase();
  const list = team
    .filter((u) => u._id !== meId)
    .filter((u) => !q || u.fullName.toLowerCase().includes(q));
  return (
    <ul className="divide-y border rounded-lg max-h-[50vh] overflow-y-auto chat-scrollbar">
      {list.map((u) => {
        const checked = selected?.has(u._id) || false;
        return (
          <li key={u._id} className="flex items-center justify-between p-2">
            <label className="flex items-center gap-3 min-w-0">
              {selectable && (
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => onToggle?.(u._id, e.target.checked)}
                  className="mr-1"
                />
              )}
              <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center font-semibold">
                {u.fullName?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="min-w-0">
                <div className="font-medium truncate">{u.fullName}</div>
                <div className="text-xs text-gray-500 truncate">{u.role}</div>
              </div>
            </label>
            <div className="flex items-center gap-2">
              <span className={`inline-block w-3 h-3 rounded-full ${statusDot(u?.presence?.status)}`} />
              {!selectable && (
                <button className="px-3 py-1 rounded bg-blue-600 text-white text-sm" onClick={() => onMessage?.(u._id)}>
                  Message
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
